import { ref } from 'vue';
import { clientEnv } from '@/shared/config/env';
import { oroMxI18n } from '@/shared/i18n/oro-mx-i18n';
import { oroMxGapI18n } from '@/shared/i18n/oro-mx-i18n-gaps';
import { oroMxPageI18n } from '@/shared/i18n/oro-mx-i18n-pages';
import { rosaMxI18n } from '@/shared/i18n/rosa-mx-i18n';
import { activeSkinModule, isOroMxSkin } from '@/shared/theme/active-skin';

export type LocaleCode = 'en-US' | 'es-ES' | 'es-MX' | 'pt-BR' | 'zh-CN' | 'zh-TW';

export interface LocaleOption {
  code: LocaleCode;
  flag: string;
  label: string;
}

export const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: 'pt-BR', flag: '🇧🇷', label: 'Português' },
  { code: 'en-US', flag: '🇺🇸', label: 'English' },
  { code: 'es-MX', flag: '🇲🇽', label: 'Español (MX)' },
  { code: 'es-ES', flag: '🇪🇸', label: 'Español' },
  { code: 'zh-CN', flag: '🇨🇳', label: '简体中文' },
  { code: 'zh-TW', flag: '🇹🇼', label: '繁體中文' },
];

/** Mexico H5 player languages. zh-CN is test-line only; production omits it. */
export const ORO_MX_LOCALES: LocaleOption[] = [
  { code: 'es-MX', flag: '🇲🇽', label: 'Español' },
  { code: 'en-US', flag: '🇺🇸', label: 'English' },
  ...(clientEnv.appEnv === 'test' ? [{ code: 'zh-CN' as const, flag: '🇨🇳', label: '简体中文' }] : []),
  { code: 'zh-TW', flag: '🇹🇼', label: '繁體中文' },
];

export const localesForCurrentSkin = (): LocaleOption[] =>
  activeSkinModule?.locale.supported ?? (isOroMxSkin ? ORO_MX_LOCALES : SUPPORTED_LOCALES);

/** 皮肤级兜底语言：B-lite 模块声明；旧皮肤 oro-mx=es-MX，其余 pt-BR。 */
const skinFallbackLocale = (): LocaleCode =>
  activeSkinModule?.locale.fallback ?? (isOroMxSkin ? 'es-MX' : 'pt-BR');

type Dictionary = Record<string, string>;

const ptBR: Dictionary = {
  'account.login': 'ENTRAR',
  'account.register': 'REGISTRO',
  'account.logout': 'Sair da conta',
  'withdraw.balance': 'Conta Saldo',
  'withdraw.rolloverHint': 'Você precisa apostar para sacar. Ver detalhes.',
  'withdraw.amount': 'Valor do Saque',
  'withdraw.method': 'Método de Retirada',
  'withdraw.name': 'Nome',
  'withdraw.cpf': 'CPF ID',
  'withdraw.submit': 'Retirar Agora',
  'withdraw.comingSoon': 'Saque real em breve (M1). UI alinhada ao GC987.',
  'account.cpf': 'CPF',
  'account.dailyLimit': 'Limite diário',
  'account.pix': 'Pix',
  'account.statusPending': 'CPF pendente',
  'account.statusVerified': 'CPF verificado · Pix ativo',
  'account.title': 'Conta',
  'account.txStatus.done': 'Concluído',
  'account.txStatus.review': 'Em análise',
  'account.txStatus.available': 'Disponível',
  'account.tx.depositPix': 'Depósito Pix',
  'account.tx.withdrawPix': 'Saque Pix',
  'account.tx.bonusReleased': 'Bônus liberado',
  'account.tx.depositDetail': 'CPF verificado · 10:24',
  'account.tx.withdrawDetail': 'Banco local · CPF',
  'account.tx.bonusDetail': 'Cashback 15%',
  'account.walletSecurityAria': 'Resumo de segurança da carteira',
  'account.quickActionsAria': 'Ações rápidas da carteira',
  'account.manage': 'Gerenciar conta',
  'account.txHistory': 'Histórico de transações',
  'account.toNextVip': 'Para VIP',
  'account.turnoverLeft': 'Rollover restante',
  'account.maxVip': 'Nível máximo atingido',
  'common.available': 'Disponível',
  'common.loading': 'Carregando…',
  'common.back': 'Voltar',
  'common.search': 'Buscar',
  'common.filter': 'Filtro',
  'common.between': 'Entre',
  'common.and': 'e',
  'common.chars': 'caracteres.',
  'deposit.fastTitle': 'Pix em segundos',
  'deposit.fastDesc': 'CPF do titular precisa bater com a conta.',
  'deposit.chooseAmount': 'Escolha o valor',
  'deposit.quickAria': 'Valores rápidos',
  'deposit.other': 'Outro',
  'deposit.cpfHolder': 'CPF do titular',
  'deposit.method': 'Método',
  'deposit.methodValue': 'Pix Copia e Cola / QR Code',
  'deposit.qrAlt': 'QR Pix gerado',
  'deposit.qrReady': 'QR Pix gerado',
  'deposit.expiresIn': 'Expira em',
  'deposit.copyCode': 'Copiar código Pix',
  'deposit.bonusTitle': 'Bônus de recarga 15%',
  'deposit.bonusDesc': 'Válido para primeiro Pix do dia.',
  'redeem.title': 'Código de resgate',
  'redeem.intro': 'Insira seu código promocional para creditar o bônus na sua carteira.',
  'redeem.codeLabel': 'Código',
  'redeem.placeholder': 'Ex.: RC-XXXXXXXXXX',
  'redeem.submit': 'Resgatar',
  'redeem.submitting': 'Resgatando...',
  'redeem.bonusOf': 'Bônus de',
  'redeem.credited': 'creditado!',
  'redeem.rollover': 'Rollover necessário:',
  'redeem.seeAudit': 'Ver auditoria',
  'redeem.errClaimed': 'Você já resgatou este código.',
  'redeem.errExhausted': 'Este código esgotou.',
  'redeem.errExpired': 'Código expirado ou ainda não disponível.',
  'redeem.errInvalid': 'Código inválido.',
  'redeem.errIpLimited': 'Limite de resgates desta rede atingido.',
  'redeem.errDeviceLimited': 'Limite de resgates deste dispositivo atingido.',
  'redeem.errGeneric': 'Não foi possível resgatar. Tente novamente.',
  'audit.title': 'Registros de auditoria',
  'audit.rolloverDone': 'Rollover concluído — saque liberado',
  'audit.completeHint': 'Complete o rollover para liberar o saque.',
  'audit.empty': 'Nenhum registro de auditoria ainda.',
  'audit.remaining': 'Falta',
  'audit.status.cancelled': 'Cancelado',
  'audit.status.completed': 'Concluído',
  'audit.status.expired': 'Expirado',
  'audit.status.inProgress': 'Em andamento',
  'audit.status.pending': 'Pendente',
  'profile.avatarsAria': 'Avatares disponíveis',
  'profile.namePlaceholder': 'Seu apelido',
  'profile.saved': 'Salvo',
  'profile.errSave': 'Não foi possível salvar. Verifique os dados e tente novamente.',
  'profile.errNetwork': 'Falha de conexão. Tente novamente.',
  'shell.backHome': 'Voltar ao início',
  'shell.selectCountry': 'Selecionar país',
  'shell.country': 'Brasil',
  'shell.openWallet': 'Abrir carteira',
  'shell.openAccount': 'Abrir conta, 18+',
  'shell.mainNav': 'Navegação principal',
  'shell.booting': 'Carregando configuração',
  'shell.bootingDesc': 'Preparando tema, idioma e regras do site.',
  'shell.unavailable': 'Aplicativo indisponível',
  'compliance.strip.aria': 'Informações de conformidade e segurança',
  'kyc.panelTitle': 'Verificação CPF',
  'kyc.panelCopy': 'Complete CPF e idade para liberar depósito, saque e benefícios.',
  'kyc.panelCta': 'Continuar verificação',
  'kyc.supportTitle': 'Ajuda KYC',
  'kyc.supportCopy': 'A verificação protege saldo, saque Pix e regras do país. Em caso de dúvida, fale com atendimento.',
  'kyc.required': 'Obrigatório',
  'kyc.reqAria': 'Requisitos de verificação',
  'kyc.cpfLabel': 'CPF obrigatório',
  'kyc.cpfValue': 'Documento válido',
  'kyc.ageLabel': '18+',
  'kyc.ageValue': 'Idade confirmada',
  'kyc.regionLabel': 'Brasil',
  'kyc.regionValue': 'Conta local',
  'promo.heroTitle': 'Promoções',
  'promo.heroSubtitle': 'Termos, rollover e prazo precisam vir do conteúdo publicado.',
  'promo.heroBadge': 'Oferta limitada',
  'promo.heroCta': 'Participar',
  'promo.actionClaim': 'Participar',
  'promo.actionRules': 'Ver regras',
  'promo.actionVenue': 'Entrar no lobby',
  'promo.supportTitle': 'Termos e atendimento',
  'promo.supportCopy': 'Atividades dependem de login, CPF/KYC e regras publicadas para o país.',
  'promo.taskTitle': 'Tarefas diárias',
  'promo.taskCopy': 'Estado de participação será carregado pela API da campanha.',
  'promo.taskCta': 'Em breve',
  'promo.actionsAria': 'Ações da promoção',
  'venue.heroTitle': 'Cassino',
  'venue.heroSubtitle': 'Mesas, limites e status de fornecedor em tempo real.',
  'venue.heroBadge': 'Live',
  'venue.heroCta': 'Abrir lobby',
  'venue.supportTitle': 'Ajuda 24h',
  'venue.supportCopy': 'Suporte, limites e jogo responsável ficam visíveis durante toda a sessão.',
  'venue.searchPlaceholder': 'Buscar jogo, salão ou provedor',
  'game.all': 'Todos',
  'game.empty': 'Nenhum jogo disponível ainda.',
  'game.loadError': 'Não foi possível carregar os jogos.',
  'game.retry': 'Tentar de novo',
  'game.loadMore': 'Carregar mais',
  'game.comingSoon': 'Em breve!',
  'action.audit': 'Auditoria',
  'action.deposit': 'Depositar Pix',
  'action.redeem': 'Resgatar',
  'action.withdraw': 'Sacar',
  'activity.welcome.title': 'Bônus de boas-vindas',
  'activity.claim': 'Abrir baú',
  'activity.claimed': 'Resgatado',
  'activity.claimSuccess': 'Bônus creditado na carteira!',
  'activity.claimableNow': 'Resgate disponível',
  'activity.day': 'Dia',
  'activity.dayPrefix': 'Dia ',
  'activity.daySuffix': '',
  'activity.details': 'Ver detalhes',
  'activity.metric.deposit': 'Depósito acumulado',
  'activity.metric.signin': 'Dias seguidos',
  'activity.empty': 'Nenhuma atividade disponível',
  'activity.toClaim': 'para resgatar',
  'activity.sectionClaim': 'Para resgatar',
  'activity.sectionMore': 'Mais atividades',
  'activity.claimCta': 'Resgatar',
  'activity.claimNeedPoints': 'Resgatar custa {amount} pontos',
  'activity.claimPointsInsufficient': 'Pontos insuficientes, não é possível resgatar',
  'activity.upTo': 'Até',
  'activity.unit.invites': 'convites',
  'activity.cat.all': 'Todas',
  'activity.cat.daily': 'Diárias',
  'activity.cat.deposit': 'Depósito',
  'activity.cat.invite': 'Convite',
  'activity.cat.newbie': 'Novato',
  'activity.cat.monthly': 'Mensal',
  'activity.detail.aria.conditions': 'Condições da atividade',
  'activity.detail.aria.mysteryDays': 'Dias de desbloqueio',
  'activity.detail.aria.overview': 'Resumo da atividade',
  'activity.detail.chests': 'baús',
  'activity.detail.claim.deposit': 'Depositar',
  'activity.detail.deposit.action': 'Ação',
  'activity.detail.deposit.bestTier': 'Maior faixa do período',
  'activity.detail.deposit.bonus': 'Bônus',
  'activity.detail.deposit.deposit': 'Depósito',
  'activity.detail.deposit.rollover': 'Rollover',
  'activity.detail.deposit.total': 'Total',
  'activity.detail.hero.dailySignin': 'Faça check-in todos os dias, mantenha a sequência e abra a recompensa do dia.',
  'activity.detail.hero.depositBonus': 'Deposite durante a janela da campanha e receba o bônus da maior faixa atingida.',
  'activity.detail.hero.default': 'Confira requisitos, recompensa e regras antes de participar.',
  'activity.detail.hero.inviteRebate': 'Convide amigos válidos, acompanhe seu progresso e abra cada baú uma única vez.',
  'activity.detail.hero.memberAppreciation': 'Uma recompensa mensal calculada pelo depósito do mês anterior.',
  'activity.detail.hero.newPlayerMystery': 'Novos membros desbloqueiam prêmios surpresa em dias específicos após o registro.',
  'activity.detail.invite.directLevel': 'Nível direto',
  'activity.detail.invite.minFriendDeposit': 'Depósito mínimo do amigo',
  'activity.detail.invite.rollover': 'Rollover do bônus',
  'activity.detail.invite.validDepth': 'Profundidade válida',
  'activity.detail.member.collectionDay': 'No dia 7 de cada mês',
  'activity.detail.member.collectionTime': 'Hora de coleta',
  'activity.detail.member.monthlyDepositAtLeast': 'Depósito mensal ≥',
  'activity.detail.mystery.prize': 'Prêmio misterioso',
  'activity.detail.overview.maxReward': 'Prêmio máximo',
  'activity.detail.overview.period': 'Período',
  'activity.detail.overview.rollover': 'Rollover',
  'activity.detail.period.monthPrefix': 'Período',
  'activity.detail.period.permanent': 'Permanente',
  'activity.detail.period.todayPrefix': 'Hoje',
  'activity.detail.rule.abuse': 'A participação é pessoal; abuso, conta compartilhada ou operação de risco pode cancelar o bônus.',
  'activity.detail.rule.dailySignin': 'O check-in é diário; se a sequência for interrompida, a próxima rodada recomeça no Dia 1.',
  'activity.detail.rule.depositBonus': 'Apenas a maior faixa atingida no período fica disponível para resgate.',
  'activity.detail.rule.inviteRebate': 'Cada baú pode ser aberto uma vez quando a quantidade de convites válidos for atingida.',
  'activity.detail.rule.memberAppreciation': 'O cálculo usa o depósito bem-sucedido do mês anterior, com coleta no dia configurado.',
  'activity.detail.rule.newPlayerMystery': 'Cada nó de dia é desbloqueado conforme a idade da conta e a soma de depósitos do período.',
  'activity.detail.rule.rollover': 'O bônus precisa ser resgatado manualmente e exige rollover 1x antes do saque.',
  'activity.detail.rolloverBonus': '1x bônus',
  'activity.detail.signin.sequence': 'Sequência de 7 dias',
  'activity.detail.state.available': 'Disponível',
  'activity.detail.state.expired': 'Expirado',
  'activity.detail.state.ineligible': 'Indisponível',
  'activity.detail.state.progress': 'Em progresso',
  'activity.detail.state.visitor': 'Visitante',
  'activity.detail.status.myState': 'Meu estado',
  'activity.detail.status.nextTarget': 'Próxima meta',
  'activity.detail.targetComplete': 'Todas as etapas concluídas',
  'vip.title': 'VIP',
  'vip.currentLevel': 'Nível atual',
  'vip.maxLevel': 'Nível máximo atingido',
  'vip.progressDeposit': 'Depósito acumulado',
  'vip.nextLevel': 'Próximo nível',
  'vip.needMore': 'Faltam',
  'vip.orTurnover': 'ou apostas de',
  'vip.bonuses': 'Recompensas VIP',
  'vip.bonus.UPGRADE': 'Bônus de upgrade',
  'vip.bonus.DAILY': 'Bônus diário',
  'vip.bonus.WEEKLY': 'Bônus semanal',
  'vip.bonus.MONTHLY': 'Bônus mensal',
  'vip.claim': 'Resgatar',
  'vip.claimed': 'Resgatado',
  'vip.unavailable': 'Indisponível',
  'vip.claimSuccess': 'Recompensa recebida!',
  'vip.levelTable': 'Tabela de níveis',
  'vip.tableLevel': 'Nível',
  'vip.tableDeposit': 'Depósito',
  'vip.tableTurnover': 'Apostas',
  'vip.tableUpgrade': 'Upgrade',
  'vip.tableDaily': 'Diário',
  'vip.tableWeekly': 'Semanal',
  'vip.tableMonthly': 'Mensal',
  'vip.history': 'Histórico',
  'vip.historyEmpty': 'Nenhum resgate ainda',
  'vip.loginPrompt': 'Entre para ver seu nível VIP e resgatar recompensas.',
  'vip.loadError': 'Não foi possível carregar os dados VIP.',
  'vip.retry': 'Tentar novamente',
  'activity.invite.copied': 'Link copiado!',
  'activity.invite.copy': 'Copiar link de convite',
  'activity.invite.title': 'Meu código de convite',
  'activity.loadError': 'Não foi possível carregar as atividades.',
  'activity.loading': 'Carregando atividades...',
  'activity.loginPrompt': 'Entre para ver as atividades e recompensas.',
  'activity.locked': 'Bloqueado',
  'activity.loginToJoin': 'Entre para participar e resgatar',
  'activity.progress': 'Convites válidos',
  'activity.retry': 'Tentar novamente',
  'activity.rules': 'Regras',
  'activity.tiers': 'Baús de recompensa',
  'agent.collectable': 'Coletável:',
  'agent.comingSoon': 'Em breve nesta versão.',
  'agent.copy': 'Copiar',
  'agent.noCommission': 'Sem comissão disponível para receber',
  'agent.open': 'Abrir',
  'agent.qrSaved': 'QR Code salvo',
  'agent.qrUnavailable': 'QR Code indisponível',
  'agent.share': 'Compartilhar',
  'agent.commission': 'Comissão',
  'agent.commissionLast': 'Última Comissão Recebida',
  'agent.commissionReceived': 'Comissão Recebida',
  'agent.commissionTotal': 'Comissão Total',
  'agent.directPerf': 'Desempenho Direto',
  'agent.directSubs': 'Subordinados Diretos',
  'agent.history': 'Histórico',
  'agent.linkCopied': 'Link copiado',
  'agent.more': 'mais',
  'agent.myId': 'MeuID:',
  'agent.myLink': 'Meu link',
  'agent.otherMembers': 'Outros Membros',
  'agent.otherPerf': 'Outro Desempenho',
  'agent.people': 'Pessoa',
  'agent.performance': 'Desempenho',
  'agent.receive': 'Receber',
  'agent.refInfo': 'Informação de Referência',
  'agent.referrerId': 'ConvidadorID:',
  'agent.save': 'Salvar',
  'agent.settleNote': 'O tempo de resolução da comissão é de 4:00-8:00 todos os dias.',
  'agent.tab.commission': 'Comissão',
  'agent.tab.link': 'Link de Convite',
  'agent.tab.network': 'Rede de Agentes',
  'agent.tab.performance': 'Desempenho',
  'agent.tab.rate': 'Taxa de Bônus',
  'agent.tab.register': 'Registrar Subordinados',
  'agent.teamTotal': 'Total de Membros da Equipe',
  'agent.totalRevenue': 'Rendimento Total',
  'agent.records.empty': 'Sem Registros',
  'agent.net.intro': 'Modelo de distribuição em vários níveis: ganhe comissão direta dos seus indicados e comissão de nível diferencial de toda a rede abaixo.',
  'agent.net.directTeam': 'Equipe direta',
  'agent.net.directTeamDesc': 'Membros que você convidou diretamente (1º nível).',
  'agent.net.otherTeam': 'Outras equipes',
  'agent.net.otherTeamDesc': 'Indicados dos seus indicados (2º nível em diante, sem limite).',
  'agent.net.formula': 'Comissão = Direta 3% + Diferencial (sua taxa − taxa do downline). Diferencial ≤ 0 não gera comissão.',
  'agent.net.teamPerf': 'Desempenho total da equipe',
  'agent.net.totalComm': 'Comissão total',
  'agent.net.directPerf': 'Desempenho direto',
  'agent.net.directComm': 'Comissão direta (3%)',
  'agent.net.otherPerf': 'Outro desempenho',
  'agent.net.otherComm': 'Comissão de nível (2%)',
  'agent.net.example': 'Exemplo oficial',
  'agent.net.exampleNote': 'A indica B1/B2/B3; B1 indica C1/C2; B3 indica C3. Apostas válidas no dia seguinte: B1=500, B2=3.000, B3=2.000, C1=1.000, C2=2.000, C3=20.000. Comissão de B1=30, B3=600; comissão total de A = 165 (direta) + 60 (nível) = 225.',
  'agent.col.id': 'ID',
  'agent.col.subordinates': 'Subordinados',
  'agent.col.bet': 'Aposta',
  'agent.col.performance': 'Desempenho',
  'agent.col.deposit': 'Depósito',
  'agent.col.time': 'Tempos',
  'agent.col.type': 'Tipos',
  'agent.col.people': 'Nº de Pessoas',
  'agent.col.commission': 'Comissão',
  'agent.filter.searchId': 'Buscar por ID',
  'agent.filter.all': 'Todos',
  'agent.commType.direct': 'Direta',
  'agent.commType.diff': 'Nível',
  'agent.rate.level': 'Nível',
  'agent.rate.threshold': 'Desempenho ≥',
  'agent.rate.value': 'Taxa',
  'agent.rate.cat.slot': 'Slot',
  'agent.rate.cat.cartas': 'Cartas',
  'agent.rate.cat.pesca': 'Pesca',
  'agent.rate.cat.cassino': 'Cassino',
  'agent.rate.pending': 'Tabela em configuração.',
  'agent.reg.note': 'Preencha as informações a seguir para concluir a abertura da conta.',
  'agent.reg.phone': 'Telefone',
  'agent.reg.password': 'Senha',
  'agent.reg.confirm': 'Confirmar senha',
  'agent.reg.submit': 'Criar',
  'agent.reg.success': 'Conta do subordinado criada',
  'agent.reg.mismatch': 'As senhas não coincidem',
  'agent.reg.invalidPhone': 'Telefone inválido',
  'agent.reg.weakPwd': 'A senha deve ter ao menos 6 caracteres',
  'app.offlineStable': 'Config local',
  'auth.action.clear': 'Limpar',
  'auth.action.close': 'Fechar',
  'auth.action.hasAccount': 'Já tem uma conta?',
  'auth.action.haveAccount': 'Já tenho conta',
  'auth.action.hide': 'Ocultar',
  'auth.action.noAccount': 'Não tem uma conta?',
  'auth.action.recover': 'Esqueceu a senha?',
  'auth.action.show': 'Mostrar',
  'auth.error.age': 'Confirme que você tem 18 anos ou mais.',
  'auth.error.displayName': 'O nome exibido deve ter no máximo 96 caracteres.',
  'auth.error.loginName': 'Número de telefone inválido',
  'auth.error.password': '6-16 caracteres',
  'auth.error.passwordMismatch': 'As senhas não conferem.',
  'auth.field.confirmPassword': 'Confirmar senha',
  'auth.field.displayName': 'Nome exibido',
  'auth.field.displayNamePlaceholder': 'Seu nome no app',
  'auth.field.loginName': 'Telefone',
  'auth.field.password': 'Senha',
  'auth.field.referralCode': 'Código de convite',
  'auth.login.heading': 'Faça login na sua conta',
  'auth.login.submit': 'Entrar',
  'auth.login.submitting': 'Entrando...',
  'auth.login.subtitle': 'Use celular, usuário ou e-mail para acessar.',
  'auth.option.age': 'Tenho 18 anos ou mais',
  'auth.option.remember': 'Lembrar Senha',
  'auth.referralApplied': 'Convite aplicado:',
  'auth.register.heading': 'Crie uma conta de jogo',
  'auth.register.rewardTag': '+R$56',
  'auth.register.submit': 'Registro',
  'auth.register.submitting': 'Registrando...',
  'auth.register.subtitle': 'Crie sua conta com celular, usuário ou e-mail.',
  'auth.security.sms': 'SMS protegido para recuperar acesso',
  'auth.sessionExpired': 'Sua sessão expirou. Entre novamente.',
  'auth.serverError.ipDenied': 'O acesso por este IP está restrito. Entre em contato com o suporte.',
  'auth.serverError.accountDisabled': 'Esta conta está desativada. Fale com o suporte.',
  'auth.serverError.invalidCredentials': 'Celular ou senha incorretos. Verifique e tente novamente.',
  'auth.serverError.smsCodeInvalid': 'O código de verificação está incorreto. Verifique e tente novamente.',
  'auth.serverError.passwordInvalid': 'Senha incorreta. Verifique e tente novamente.',
  'auth.serverError.userNotFound': 'Esta conta não existe. Crie uma conta para continuar.',
  'auth.serverError.loginFailed': 'Não foi possível entrar agora.',
  'auth.serverError.loginValidation': 'Informe uma conta válida e senha com pelo menos 6 caracteres.',
  'auth.serverError.rateLimited': 'Muitas tentativas. Aguarde um pouco e tente novamente.',
  'auth.serverError.registerFailed': 'Não foi possível criar a conta agora.',
  'auth.serverError.registerValidation': 'Conta ou telefone já existe. Entre com a conta.',
  'auth.serverError.tenantScope': 'Este site não está disponível para login agora.',
  'auth.serverError.unavailable': 'Serviço de autenticação indisponível. Tente novamente em instantes.',
  'auth.tab.login': 'Entrar',
  'auth.tab.register': 'Registro',
  'auth.tabsLabel': 'Acesso',
  'auth.trust.licensed': 'Licenciado',
  'auth.trust.licensedValue': 'SPA/MF',
  'auth.trust.phone': 'Celular',
  'auth.trust.phoneValue': 'SMS protegido',
  'auth.trust.safe': 'Jogo seguro',
  'auth.trust.safeValue': 'Limites ativos',
  'common.active': 'Ativo',
  'common.pending': 'Pendente',
  'common.seeAll': 'Ver tudo',
  'common.unavailable': 'Indisponível',
  'common.verified': 'Verificado',
  'compliance.license.br': 'Licenciado para operação regulada. Jogue com responsabilidade.',
  'compliance.license.mx': 'Operación para México. Juega con responsabilidad. +18.',
  'compliance.responsibleGaming.entry': 'Jogo responsável',
  'deposit.mxSkeletonHint': 'A réplica 1:1 do Oro Casino está pendente. Este painel usa MXN e o canal SIMULATE.',
  'deposit.mxSkeletonTitle': 'Depósito MXN',
  'compliance.strip.licensed': 'Licenciado',
  'compliance.strip.pix': 'Pix ativo',
  'compliance.strip.cpf': 'CPF verificado',
  'compliance.strip.responsible': 'Jogo responsável',
  'deposit.title': 'Depositar com Pix',
  'games.title': 'Jogos',
  'home.title': 'Inicio',
  'home.carousel.aria': 'Atividades em destaque',
  'home.carousel.select': 'Selecionar atividade',
  'home.preview.cta': 'Entrar agora',
  'home.preview.eyebrow': 'Destaque',
  'home.preview.title': 'Promoção',
  'home.preview.endsIn': 'Termina em',
  'home.venues.title': 'Salões de jogos',
  'home.venues.aria': 'Salões de jogos',
  'home.venues.unit': 'jogos',
  'home.announce.aria': 'Avisos e novidades',
  'announce.deposit': 'Usuário {id} depositou {amount}',
  'home.announce.payout': 'Saques via Pix processados em até 3 minutos.',
  'home.announce.bonus': '1º depósito com 100% de bônus, até R$ 500.',
  'home.announce.draw': 'Sorteio da sorte às 21h: R$ 10.000 em prêmios.',
  'home.slots.title': 'Slots populares',
  'language.title': 'Idioma',
  'nav.account': 'Perfil',
  'nav.activities': 'Atividades',
  'nav.agent': 'Agente',
  'agent.centerAria': 'Centro de agente',
  'nav.casino': 'Cassino',
  'nav.deposit': 'Depósito',
  'nav.games': 'Esportes',
  'nav.home': 'Inicio',
  'nav.promo': 'Promoção',
  'nav.venue': 'Salões',
  'nav.withdraw': 'Saque',
  'nav.perfil': 'Perfil',
  'profile.account': 'Conta',
  'profile.edit': 'Editar perfil',
  'profile.nickname': 'Apelido',
  'profile.pickAvatar': 'Escolha um avatar',
  'profile.save': 'Salvar',
  'profile.title': 'Meus dados',
  'wallet.balance': 'Saldo disponível',
  'wallet.refresh': 'Atualizar saldo',
  'wallet.history': 'Histórico',
  'wallet.playReal': 'Jogue com saldo real',
  'wallet.quickActions': 'Ações rápidas',
  'wallet.realBalance': 'Saldo real',
  'wallet.recentTx': 'Últimas transações',
  'wallet.released': 'Saldo liberado para saque',
  'wallet.rolloverPending': 'Rollover pendente',
  'wallet.title': 'Carteira',
  'wallet.loading': 'Carregando transações...',
  'wallet.empty': 'Nenhuma transação ainda',
  'wallet.loadError': 'Não foi possível carregar as transações.',
  'wallet.retry': 'Tentar novamente',
  'wallet.loginRequired': 'Entre para ver seu histórico',
};

const enUS: Dictionary = {
  'account.login': 'Sign in',
  'account.register': 'Sign up',
  'account.logout': 'Log out',
  'account.cpf': 'CPF',
  'account.dailyLimit': 'Daily limit',
  'account.pix': 'Pix',
  'account.statusPending': 'CPF pending',
  'account.statusVerified': 'CPF verified · Pix active',
  'account.title': 'Account',
  'account.txStatus.done': 'Completed',
  'account.txStatus.review': 'Under review',
  'account.txStatus.available': 'Available',
  'account.tx.depositPix': 'Pix deposit',
  'account.tx.withdrawPix': 'Pix withdrawal',
  'account.tx.bonusReleased': 'Bonus released',
  'account.tx.depositDetail': 'CPF verified · 10:24',
  'account.tx.withdrawDetail': 'Local bank · CPF',
  'account.tx.bonusDetail': 'Cashback 15%',
  'account.walletSecurityAria': 'Wallet security summary',
  'account.quickActionsAria': 'Wallet quick actions',
  'account.manage': 'Account',
  'account.txHistory': 'Transaction history',
  'account.toNextVip': 'To VIP',
  'account.turnoverLeft': 'Rollover left',
  'account.maxVip': 'Max level reached',
  'common.available': 'Available',
  'common.loading': 'Loading…',
  'common.back': 'Back',
  'common.search': 'Search',
  'common.filter': 'Filter',
  'common.between': 'Between',
  'common.and': 'and',
  'common.chars': 'characters.',
  'deposit.fastTitle': 'Pix in seconds',
  'deposit.fastDesc': 'The holder CPF must match the account.',
  'deposit.chooseAmount': 'Choose the amount',
  'deposit.quickAria': 'Quick amounts',
  'deposit.other': 'Other',
  'deposit.cpfHolder': 'Holder CPF',
  'deposit.method': 'Method',
  'deposit.methodValue': 'Pix Copy & Paste / QR Code',
  'deposit.qrAlt': 'Pix QR generated',
  'deposit.qrReady': 'Pix QR generated',
  'deposit.expiresIn': 'Expires in',
  'deposit.copyCode': 'Copy Pix code',
  'deposit.bonusTitle': 'Top-up bonus 15%',
  'deposit.bonusDesc': 'Valid for the first Pix of the day.',
  'redeem.title': 'Redeem code',
  'redeem.intro': 'Enter your promo code to credit the bonus to your wallet.',
  'redeem.codeLabel': 'Code',
  'redeem.placeholder': 'e.g.: RC-XXXXXXXXXX',
  'redeem.submit': 'Redeem',
  'redeem.submitting': 'Redeeming...',
  'redeem.bonusOf': 'Bonus of',
  'redeem.credited': 'credited!',
  'redeem.rollover': 'Required rollover:',
  'redeem.seeAudit': 'View audit',
  'redeem.errClaimed': 'You have already redeemed this code.',
  'redeem.errExhausted': 'This code is exhausted.',
  'redeem.errExpired': 'Code expired or not available yet.',
  'redeem.errInvalid': 'Invalid code.',
  'redeem.errIpLimited': 'Redemption limit reached for this network.',
  'redeem.errDeviceLimited': 'Redemption limit reached for this device.',
  'redeem.errGeneric': 'Could not redeem. Please try again.',
  'audit.title': 'Audit records',
  'audit.rolloverDone': 'Rollover completed — withdrawal unlocked',
  'audit.completeHint': 'Complete the rollover to unlock withdrawal.',
  'audit.empty': 'No audit records yet.',
  'audit.remaining': 'Remaining',
  'audit.status.cancelled': 'Cancelled',
  'audit.status.completed': 'Completed',
  'audit.status.expired': 'Expired',
  'audit.status.inProgress': 'In progress',
  'audit.status.pending': 'Pending',
  'profile.avatarsAria': 'Available avatars',
  'profile.namePlaceholder': 'Your nickname',
  'profile.saved': 'Saved',
  'profile.errSave': 'Could not save. Check the details and try again.',
  'profile.errNetwork': 'Connection failed. Please try again.',
  'shell.backHome': 'Back to home',
  'shell.selectCountry': 'Select country',
  'shell.country': 'Brazil',
  'shell.openWallet': 'Open wallet',
  'shell.openAccount': 'Open account, 18+',
  'shell.mainNav': 'Main navigation',
  'shell.booting': 'Loading configuration',
  'shell.bootingDesc': 'Preparing theme, language and site rules.',
  'shell.unavailable': 'App unavailable',
  'compliance.strip.aria': 'Compliance and security information',
  'kyc.panelTitle': 'CPF verification',
  'kyc.panelCopy': 'Complete CPF and age to unlock deposit, withdrawal and benefits.',
  'kyc.panelCta': 'Continue verification',
  'kyc.supportTitle': 'KYC help',
  'kyc.supportCopy': 'Verification protects your balance, Pix withdrawal and country rules. If in doubt, contact support.',
  'kyc.required': 'Required',
  'kyc.reqAria': 'Verification requirements',
  'kyc.cpfLabel': 'CPF required',
  'kyc.cpfValue': 'Valid document',
  'kyc.ageLabel': '18+',
  'kyc.ageValue': 'Age confirmed',
  'kyc.regionLabel': 'Brazil',
  'kyc.regionValue': 'Local account',
  'promo.heroTitle': 'Promotions',
  'promo.heroSubtitle': 'Terms, rollover and deadline must come from published content.',
  'promo.heroBadge': 'Limited offer',
  'promo.heroCta': 'Join',
  'promo.actionClaim': 'Join',
  'promo.actionRules': 'View rules',
  'promo.actionVenue': 'Enter lobby',
  'promo.supportTitle': 'Terms and support',
  'promo.supportCopy': 'Activities depend on login, CPF/KYC and rules published for the country.',
  'promo.taskTitle': 'Daily tasks',
  'promo.taskCopy': 'Participation status will be loaded by the campaign API.',
  'promo.taskCta': 'Coming soon',
  'promo.actionsAria': 'Promotion actions',
  'venue.heroTitle': 'Casino',
  'venue.heroSubtitle': 'Tables, limits and provider status in real time.',
  'venue.heroBadge': 'Live',
  'venue.heroCta': 'Open lobby',
  'venue.supportTitle': '24h help',
  'venue.supportCopy': 'Support, limits and responsible gaming stay visible throughout the session.',
  'venue.searchPlaceholder': 'Search game, lounge or provider',
  'game.all': 'All',
  'game.empty': 'No games available yet.',
  'game.loadError': 'Could not load games.',
  'game.retry': 'Try again',
  'game.loadMore': 'Load more',
  'game.comingSoon': 'Coming soon!',
  'action.audit': 'Audit',
  'action.deposit': 'Deposit Pix',
  'action.redeem': 'Redeem',
  'action.withdraw': 'Withdraw',
  'activity.welcome.title': 'Welcome bonus',
  'activity.claim': 'Open chest',
  'activity.claimed': 'Claimed',
  'activity.claimSuccess': 'Bonus credited to your wallet!',
  'activity.claimableNow': 'Ready to claim',
  'activity.day': 'Day',
  'activity.dayPrefix': 'Day ',
  'activity.daySuffix': '',
  'activity.details': 'View details',
  'activity.metric.deposit': 'Total deposited',
  'activity.metric.signin': 'Day streak',
  'activity.empty': 'No activities available',
  'activity.toClaim': 'to claim',
  'activity.sectionClaim': 'Ready to claim',
  'activity.sectionMore': 'More activities',
  'activity.claimCta': 'Claim',
  'activity.claimNeedPoints': 'Claiming costs {amount} points',
  'activity.claimPointsInsufficient': 'Not enough points to claim',
  'activity.upTo': 'Up to',
  'activity.unit.invites': 'invites',
  'activity.cat.all': 'All',
  'activity.cat.daily': 'Daily',
  'activity.cat.deposit': 'Deposit',
  'activity.cat.invite': 'Invite',
  'activity.cat.newbie': 'Newbie',
  'activity.cat.monthly': 'Monthly',
  'activity.detail.aria.conditions': 'Activity conditions',
  'activity.detail.aria.mysteryDays': 'Unlock days',
  'activity.detail.aria.overview': 'Activity summary',
  'activity.detail.chests': 'chests',
  'activity.detail.claim.deposit': 'Deposit',
  'activity.detail.deposit.action': 'Action',
  'activity.detail.deposit.bestTier': 'Highest tier in this period',
  'activity.detail.deposit.bonus': 'Bonus',
  'activity.detail.deposit.deposit': 'Deposit',
  'activity.detail.deposit.rollover': 'Rollover',
  'activity.detail.deposit.total': 'Total',
  'activity.detail.hero.dailySignin': 'Check in every day, keep your streak and open the reward for the day.',
  'activity.detail.hero.depositBonus': 'Deposit during the campaign window and receive the bonus from your highest reached tier.',
  'activity.detail.hero.default': 'Review requirements, reward and rules before joining.',
  'activity.detail.hero.inviteRebate': 'Invite valid friends, track your progress and open each chest once.',
  'activity.detail.hero.memberAppreciation': 'A monthly reward calculated from the previous month deposits.',
  'activity.detail.hero.newPlayerMystery': 'New members unlock surprise prizes on specific days after registration.',
  'activity.detail.invite.directLevel': 'Direct level',
  'activity.detail.invite.minFriendDeposit': 'Friend minimum deposit',
  'activity.detail.invite.rollover': 'Bonus rollover',
  'activity.detail.invite.validDepth': 'Valid depth',
  'activity.detail.member.collectionDay': 'On the 7th day of each month',
  'activity.detail.member.collectionTime': 'Collection time',
  'activity.detail.member.monthlyDepositAtLeast': 'Monthly deposit ≥',
  'activity.detail.mystery.prize': 'Mystery prize',
  'activity.detail.overview.maxReward': 'Max prize',
  'activity.detail.overview.period': 'Period',
  'activity.detail.overview.rollover': 'Rollover',
  'activity.detail.period.monthPrefix': 'Period',
  'activity.detail.period.permanent': 'Permanent',
  'activity.detail.period.todayPrefix': 'Today',
  'activity.detail.rule.abuse': 'Participation is personal; abuse, shared accounts or risky activity may cancel the bonus.',
  'activity.detail.rule.dailySignin': 'Check-in is daily; if the streak is interrupted, the next round restarts from Day 1.',
  'activity.detail.rule.depositBonus': 'Only the highest tier reached in the period becomes available to claim.',
  'activity.detail.rule.inviteRebate': 'Each chest can be opened once when the required number of valid invites is reached.',
  'activity.detail.rule.memberAppreciation': 'The calculation uses successful deposits from the previous month, with collection on the configured day.',
  'activity.detail.rule.newPlayerMystery': 'Each day node unlocks based on account age and total deposits during the period.',
  'activity.detail.rule.rollover': 'The bonus must be claimed manually and requires 1x rollover before withdrawal.',
  'activity.detail.rolloverBonus': '1x bonus',
  'activity.detail.signin.sequence': '7-day streak',
  'activity.detail.state.available': 'Available',
  'activity.detail.state.expired': 'Expired',
  'activity.detail.state.ineligible': 'Unavailable',
  'activity.detail.state.progress': 'In progress',
  'activity.detail.state.visitor': 'Guest',
  'activity.detail.status.myState': 'My status',
  'activity.detail.status.nextTarget': 'Next target',
  'activity.detail.targetComplete': 'All steps completed',
  'vip.title': 'VIP',
  'vip.currentLevel': 'Current level',
  'vip.maxLevel': 'Max level reached',
  'vip.progressDeposit': 'Total deposits',
  'vip.nextLevel': 'Next level',
  'vip.needMore': 'Need',
  'vip.orTurnover': 'or wagering of',
  'vip.bonuses': 'VIP rewards',
  'vip.bonus.UPGRADE': 'Upgrade bonus',
  'vip.bonus.DAILY': 'Daily bonus',
  'vip.bonus.WEEKLY': 'Weekly bonus',
  'vip.bonus.MONTHLY': 'Monthly bonus',
  'vip.claim': 'Claim',
  'vip.claimed': 'Claimed',
  'vip.unavailable': 'Unavailable',
  'vip.claimSuccess': 'Reward received!',
  'vip.levelTable': 'Level table',
  'vip.tableLevel': 'Level',
  'vip.tableDeposit': 'Deposit',
  'vip.tableTurnover': 'Wagering',
  'vip.tableUpgrade': 'Upgrade',
  'vip.tableDaily': 'Daily',
  'vip.tableWeekly': 'Weekly',
  'vip.tableMonthly': 'Monthly',
  'vip.history': 'History',
  'vip.historyEmpty': 'No claims yet',
  'vip.loginPrompt': 'Sign in to see your VIP level and claim rewards.',
  'vip.loadError': 'Could not load VIP data.',
  'vip.retry': 'Try again',
  'activity.invite.copied': 'Link copied!',
  'activity.invite.copy': 'Copy invite link',
  'activity.invite.title': 'My invite code',
  'activity.loadError': 'Could not load activities.',
  'activity.loading': 'Loading activities...',
  'activity.loginPrompt': 'Sign in to view activities and rewards.',
  'activity.locked': 'Locked',
  'activity.loginToJoin': 'Sign in to join and claim',
  'activity.progress': 'Valid invites',
  'activity.retry': 'Try again',
  'activity.rules': 'Rules',
  'activity.tiers': 'Reward chests',
  'agent.collectable': 'Collectable:',
  'agent.comingSoon': 'Coming soon in this version.',
  'agent.copy': 'Copy',
  'agent.noCommission': 'No commission available to claim',
  'agent.open': 'Open',
  'agent.qrSaved': 'QR code saved',
  'agent.qrUnavailable': 'QR code unavailable',
  'agent.share': 'Share',
  'agent.commission': 'Commission',
  'agent.commissionLast': 'Last commission received',
  'agent.commissionReceived': 'Commission received',
  'agent.commissionTotal': 'Total commission',
  'agent.directPerf': 'Direct performance',
  'agent.directSubs': 'Direct subordinates',
  'agent.history': 'History',
  'agent.linkCopied': 'Link copied',
  'agent.more': 'more',
  'agent.myId': 'My ID:',
  'agent.myLink': 'My link',
  'agent.otherMembers': 'Other members',
  'agent.otherPerf': 'Other performance',
  'agent.people': 'People',
  'agent.performance': 'Performance',
  'agent.receive': 'Receive',
  'agent.refInfo': 'Referral information',
  'agent.referrerId': 'Referrer ID:',
  'agent.save': 'Save',
  'agent.settleNote': 'Commission is settled daily between 4:00-8:00.',
  'agent.tab.commission': 'Commission',
  'agent.tab.link': 'Invite Link',
  'agent.tab.network': 'Agent Network',
  'agent.tab.performance': 'Performance',
  'agent.tab.rate': 'Bonus Rate',
  'agent.tab.register': 'Register Subordinates',
  'agent.teamTotal': 'Total team members',
  'agent.totalRevenue': 'Total revenue',
  'agent.records.empty': 'No records',
  'agent.net.intro': 'Multi-level distribution model: earn direct commission from your referrals and level-difference commission from the whole network below you.',
  'agent.net.directTeam': 'Direct team',
  'agent.net.directTeamDesc': 'Members you invited directly (level 1).',
  'agent.net.otherTeam': 'Other teams',
  'agent.net.otherTeamDesc': 'Referrals of your referrals (level 2 and beyond, unlimited).',
  'agent.net.formula': 'Commission = Direct 3% + Difference (your rate − downline rate). Difference ≤ 0 earns nothing.',
  'agent.net.teamPerf': 'Total team performance',
  'agent.net.totalComm': 'Total commission',
  'agent.net.directPerf': 'Direct performance',
  'agent.net.directComm': 'Direct commission (3%)',
  'agent.net.otherPerf': 'Other performance',
  'agent.net.otherComm': 'Level commission (2%)',
  'agent.net.example': 'Official example',
  'agent.net.exampleNote': 'A invites B1/B2/B3; B1 invites C1/C2; B3 invites C3. Next-day valid bets: B1=500, B2=3,000, B3=2,000, C1=1,000, C2=2,000, C3=20,000. B1 earns 30, B3 earns 600; A total = 165 (direct) + 60 (level) = 225.',
  'agent.col.id': 'ID',
  'agent.col.subordinates': 'Subordinates',
  'agent.col.bet': 'Bet',
  'agent.col.performance': 'Performance',
  'agent.col.deposit': 'Deposit',
  'agent.col.time': 'Time',
  'agent.col.type': 'Type',
  'agent.col.people': 'People',
  'agent.col.commission': 'Commission',
  'agent.filter.searchId': 'Search by ID',
  'agent.filter.all': 'All',
  'agent.commType.direct': 'Direct',
  'agent.commType.diff': 'Level',
  'agent.rate.level': 'Level',
  'agent.rate.threshold': 'Performance ≥',
  'agent.rate.value': 'Rate',
  'agent.rate.cat.slot': 'Slot',
  'agent.rate.cat.cartas': 'Cards',
  'agent.rate.cat.pesca': 'Fishing',
  'agent.rate.cat.cassino': 'Casino',
  'agent.rate.pending': 'Table being configured.',
  'agent.reg.note': 'Fill in the information below to finish opening the account.',
  'agent.reg.phone': 'Phone',
  'agent.reg.password': 'Password',
  'agent.reg.confirm': 'Confirm password',
  'agent.reg.submit': 'Create',
  'agent.reg.success': 'Subordinate account created',
  'agent.reg.mismatch': 'Passwords do not match',
  'agent.reg.invalidPhone': 'Invalid phone number',
  'agent.reg.weakPwd': 'Password must be at least 6 characters',
  'app.offlineStable': 'Local config',
  'auth.action.clear': 'Clear',
  'auth.action.close': 'Close',
  'auth.action.hasAccount': 'Already have an account?',
  'auth.action.haveAccount': 'I already have an account',
  'auth.action.hide': 'Hide',
  'auth.action.noAccount': "Don't have an account?",
  'auth.action.recover': 'Forgot password?',
  'auth.action.show': 'Show',
  'auth.error.displayName': 'The display name must be at most 96 characters.',
  'auth.error.loginName': 'Invalid phone number',
  'auth.error.password': '6-16 characters',
  'auth.error.passwordMismatch': 'The passwords do not match.',
  'auth.field.confirmPassword': 'Confirm password',
  'auth.field.displayName': 'Display name',
  'auth.field.displayNamePlaceholder': 'Your name in the app',
  'auth.field.loginName': 'Phone',
  'auth.field.password': 'Password',
  'auth.login.heading': 'Log in to your account',
  'auth.login.submit': 'Sign in',
  'auth.login.submitting': 'Signing in...',
  'auth.login.subtitle': 'Use your phone, username or email to sign in.',
  'auth.error.age': 'Confirm that you are 18 or older.',
  'auth.field.referralCode': 'Invite code',
  'auth.option.age': "I'm 18 or older",
  'auth.option.remember': 'Remember password',
  'auth.referralApplied': 'Invite applied:',
  'auth.register.heading': 'Create a gaming account',
  'auth.register.rewardTag': '+R$56',
  'auth.register.submit': 'Register',
  'auth.register.submitting': 'Registering...',
  'auth.register.subtitle': 'Create your account with phone, username or email.',
  'auth.security.sms': 'Protected SMS to recover access',
  'auth.sessionExpired': 'Your session has expired. Please sign in again.',
  'auth.serverError.ipDenied': 'Login from this IP is restricted. Please contact customer support.',
  'auth.serverError.accountDisabled': 'This account is disabled. Please contact customer support.',
  'auth.serverError.invalidCredentials': 'Incorrect phone or password. Check and try again.',
  'auth.serverError.smsCodeInvalid': 'The verification code is incorrect. Check it and try again.',
  'auth.serverError.passwordInvalid': 'Incorrect password. Check and try again.',
  'auth.serverError.userNotFound': 'This account does not exist. Please register first.',
  'auth.serverError.loginFailed': "Couldn't sign in right now.",
  'auth.serverError.loginValidation': 'Enter a valid account and a password with at least 6 characters.',
  'auth.serverError.rateLimited': 'Too many attempts. Wait a moment and try again.',
  'auth.serverError.registerFailed': "Couldn't create the account right now.",
  'auth.serverError.registerValidation': 'Account or phone already exists. Please sign in.',
  'auth.serverError.tenantScope': 'This site is not available for login right now.',
  'auth.serverError.unavailable': 'Authentication service unavailable. Please try again shortly.',
  'auth.tab.login': 'Sign in',
  'auth.tab.register': 'Register',
  'auth.tabsLabel': 'Access',
  'auth.trust.licensed': 'Licensed',
  'auth.trust.licensedValue': 'SPA/MF',
  'auth.trust.phone': 'Mobile',
  'auth.trust.phoneValue': 'Protected SMS',
  'auth.trust.safe': 'Safe play',
  'auth.trust.safeValue': 'Active limits',
  'campaign.deposit-wed.cta': 'Deposit now',
  'campaign.deposit-wed.eyebrow': 'Match Wednesday',
  'campaign.deposit-wed.reward': 'Up to R$ 100',
  'campaign.deposit-wed.subtitle': 'Deposit on Wednesday and get an extra bonus.',
  'campaign.deposit-wed.title': 'Deposit bonus',
  'campaign.member-day.cta': 'Check reward',
  'campaign.member-day.eyebrow': 'Every 7th',
  'campaign.member-day.reward': 'Up to R$ 100',
  'campaign.member-day.subtitle': "A reward for last month's deposits.",
  'campaign.member-day.title': 'Member appreciation',
  'campaign.mystery-newbie.cta': 'Open prize',
  'campaign.mystery-newbie.eyebrow': 'Newbie bonus',
  'campaign.mystery-newbie.reward': 'Up to R$ 888',
  'campaign.mystery-newbie.subtitle': 'Deposit in your first days and open surprise prizes.',
  'campaign.mystery-newbie.title': 'Mystery prize',
  'campaign.weekend-pix.cta': 'Deposit with Pix',
  'campaign.weekend-pix.eyebrow': 'Weekend',
  'campaign.weekend-pix.reward': 'Up to R$ 500',
  'campaign.weekend-pix.subtitle': 'Live football, casino and 50 free spins.',
  'campaign.weekend-pix.title': 'Game on & fun',
  'common.active': 'Active',
  'common.pending': 'Pending',
  'common.seeAll': 'See all',
  'common.unavailable': 'Unavailable',
  'common.verified': 'Verified',
  'compliance.license.br': 'Licensed for regulated operation. Play responsibly.',
  'compliance.license.mx': 'Mexico operation. Play responsibly. 18+.',
  'compliance.responsibleGaming.entry': 'Responsible gaming',
  'deposit.mxSkeletonHint': 'The 1:1 Oro Casino skin is pending source capture. This panel uses MXN and the SIMULATE channel.',
  'deposit.mxSkeletonTitle': 'MXN deposit',
  'compliance.strip.licensed': 'Licensed',
  'compliance.strip.pix': 'Pix active',
  'compliance.strip.cpf': 'CPF verified',
  'compliance.strip.responsible': 'Responsible gaming',
  'deposit.title': 'Deposit with Pix',
  'games.title': 'Games',
  'home.title': 'Home',
  'home.carousel.aria': 'Featured promotions',
  'home.carousel.select': 'Select promotion',
  'home.preview.cta': 'Enter now',
  'home.preview.eyebrow': 'Featured',
  'home.preview.title': 'Promotion',
  'home.preview.endsIn': 'Ends in',
  'home.venues.title': 'Game lounges',
  'home.venues.aria': 'Game lounges',
  'home.venues.unit': 'games',
  'home.announce.aria': 'Announcements',
  'announce.deposit': 'User {id} deposited {amount}',
  'home.announce.payout': 'Pix withdrawals processed within 3 minutes.',
  'home.announce.bonus': '100% bonus on your first deposit, up to R$ 500.',
  'home.announce.draw': 'Lucky draw at 9 PM: R$ 10,000 in prizes.',
  'home.slots.title': 'Popular slots',
  'language.title': 'Language',
  'nav.account': 'My account',
  'nav.activities': 'Activities',
  'nav.agent': 'Agent',
  'agent.centerAria': 'Agent center',
  'nav.casino': 'Casino',
  'nav.deposit': 'PIX',
  'nav.games': 'Sports',
  'nav.home': 'Home',
  'nav.venue': 'Lobby',
  'profile.account': 'Account',
  'profile.edit': 'Edit profile',
  'profile.nickname': 'Nickname',
  'profile.pickAvatar': 'Choose an avatar',
  'profile.save': 'Save',
  'profile.title': 'My profile',
  'wallet.balance': 'Available balance',
  'wallet.refresh': 'Refresh balance',
  'wallet.history': 'History',
  'wallet.playReal': 'Play with real balance',
  'wallet.quickActions': 'Quick actions',
  'wallet.realBalance': 'Real balance',
  'wallet.recentTx': 'Recent transactions',
  'wallet.released': 'Balance available for withdrawal',
  'wallet.rolloverPending': 'Rollover pending',
  'wallet.title': 'Wallet',
  'wallet.loading': 'Loading transactions...',
  'wallet.empty': 'No transactions yet',
  'wallet.loadError': 'Could not load transactions.',
  'wallet.retry': 'Try again',
  'wallet.loginRequired': 'Sign in to see your history',
};

const esES: Dictionary = {
  'account.login': 'Entrar',
  'account.register': 'Registrarse',
  'account.logout': 'Cerrar sesión',
  'account.cpf': 'CPF',
  'account.dailyLimit': 'Límite diario',
  'account.pix': 'Pix',
  'account.statusPending': 'CPF pendiente',
  'account.statusVerified': 'CPF verificado · Pix activo',
  'account.title': 'Cuenta',
  'account.txStatus.done': 'Completado',
  'account.txStatus.review': 'En revisión',
  'account.txStatus.available': 'Disponible',
  'account.tx.depositPix': 'Depósito Pix',
  'account.tx.withdrawPix': 'Retiro Pix',
  'account.tx.bonusReleased': 'Bono liberado',
  'account.tx.depositDetail': 'CPF verificado · 10:24',
  'account.tx.withdrawDetail': 'Banco local · CPF',
  'account.tx.bonusDetail': 'Cashback 15%',
  'account.walletSecurityAria': 'Resumen de seguridad de la cartera',
  'account.quickActionsAria': 'Acciones rápidas de la cartera',
  'account.manage': 'Gestión de cuenta',
  'account.txHistory': 'Historial de transacciones',
  'account.toNextVip': 'Para VIP',
  'account.turnoverLeft': 'Rollover restante',
  'account.maxVip': 'Nivel máximo alcanzado',
  'common.available': 'Disponible',
  'common.loading': 'Cargando…',
  'common.back': 'Volver',
  'common.search': 'Buscar',
  'common.filter': 'Filtro',
  'common.between': 'Entre',
  'common.and': 'y',
  'common.chars': 'caracteres.',
  'deposit.fastTitle': 'Pix en segundos',
  'deposit.fastDesc': 'El CPF del titular debe coincidir con la cuenta.',
  'deposit.chooseAmount': 'Elige el monto',
  'deposit.quickAria': 'Montos rápidos',
  'deposit.other': 'Otro',
  'deposit.cpfHolder': 'CPF del titular',
  'deposit.method': 'Método',
  'deposit.methodValue': 'Pix Copia y Pega / QR Code',
  'deposit.qrAlt': 'QR Pix generado',
  'deposit.qrReady': 'QR Pix generado',
  'deposit.expiresIn': 'Expira en',
  'deposit.copyCode': 'Copiar código Pix',
  'deposit.bonusTitle': 'Bono de recarga 15%',
  'deposit.bonusDesc': 'Válido para el primer Pix del día.',
  'redeem.title': 'Código de canje',
  'redeem.intro': 'Ingresa tu código promocional para acreditar el bono en tu cartera.',
  'redeem.codeLabel': 'Código',
  'redeem.placeholder': 'Ej.: RC-XXXXXXXXXX',
  'redeem.submit': 'Canjear',
  'redeem.submitting': 'Canjeando...',
  'redeem.bonusOf': 'Bono de',
  'redeem.credited': '¡acreditado!',
  'redeem.rollover': 'Rollover necesario:',
  'redeem.seeAudit': 'Ver auditoría',
  'redeem.errClaimed': 'Ya canjeaste este código.',
  'redeem.errExhausted': 'Este código se agotó.',
  'redeem.errExpired': 'Código expirado o aún no disponible.',
  'redeem.errInvalid': 'Código inválido.',
  'redeem.errIpLimited': 'Límite de canjes de esta red alcanzado.',
  'redeem.errDeviceLimited': 'Límite de canjes de este dispositivo alcanzado.',
  'redeem.errGeneric': 'No se pudo canjear. Inténtalo de nuevo.',
  'audit.title': 'Registros de auditoría',
  'audit.rolloverDone': 'Rollover completado — retiro liberado',
  'audit.completeHint': 'Completa el rollover para liberar el retiro.',
  'audit.empty': 'Aún no hay registros de auditoría.',
  'audit.remaining': 'Falta',
  'audit.status.cancelled': 'Cancelado',
  'audit.status.completed': 'Completado',
  'audit.status.expired': 'Expirado',
  'audit.status.inProgress': 'En progreso',
  'audit.status.pending': 'Pendiente',
  'profile.avatarsAria': 'Avatares disponibles',
  'profile.namePlaceholder': 'Tu apodo',
  'profile.saved': 'Guardado',
  'profile.errSave': 'No se pudo guardar. Verifica los datos e inténtalo de nuevo.',
  'profile.errNetwork': 'Fallo de conexión. Inténtalo de nuevo.',
  'shell.backHome': 'Volver al inicio',
  'shell.selectCountry': 'Seleccionar país',
  'shell.country': 'Brasil',
  'shell.openWallet': 'Abrir cartera',
  'shell.openAccount': 'Abrir cuenta, 18+',
  'shell.mainNav': 'Navegación principal',
  'shell.booting': 'Cargando configuración',
  'shell.bootingDesc': 'Preparando tema, idioma y reglas del sitio.',
  'shell.unavailable': 'Aplicación no disponible',
  'compliance.strip.aria': 'Información de conformidad y seguridad',
  'kyc.panelTitle': 'Verificación CPF',
  'kyc.panelCopy': 'Completa CPF y edad para liberar depósito, retiro y beneficios.',
  'kyc.panelCta': 'Continuar verificación',
  'kyc.supportTitle': 'Ayuda KYC',
  'kyc.supportCopy': 'La verificación protege saldo, retiro Pix y reglas del país. Si tienes dudas, contacta soporte.',
  'kyc.required': 'Obligatorio',
  'kyc.reqAria': 'Requisitos de verificación',
  'kyc.cpfLabel': 'CPF obligatorio',
  'kyc.cpfValue': 'Documento válido',
  'kyc.ageLabel': '18+',
  'kyc.ageValue': 'Edad confirmada',
  'kyc.regionLabel': 'Brasil',
  'kyc.regionValue': 'Cuenta local',
  'promo.heroTitle': 'Promociones',
  'promo.heroSubtitle': 'Términos, rollover y plazo deben venir del contenido publicado.',
  'promo.heroBadge': 'Oferta limitada',
  'promo.heroCta': 'Participar',
  'promo.actionClaim': 'Participar',
  'promo.actionRules': 'Ver reglas',
  'promo.actionVenue': 'Entrar al lobby',
  'promo.supportTitle': 'Términos y atención',
  'promo.supportCopy': 'Las actividades dependen de inicio de sesión, CPF/KYC y reglas publicadas para el país.',
  'promo.taskTitle': 'Tareas diarias',
  'promo.taskCopy': 'El estado de participación será cargado por la API de la campaña.',
  'promo.taskCta': 'Próximamente',
  'promo.actionsAria': 'Acciones de la promoción',
  'venue.heroTitle': 'Casino',
  'venue.heroSubtitle': 'Mesas, límites y estado del proveedor en tiempo real.',
  'venue.heroBadge': 'Live',
  'venue.heroCta': 'Abrir lobby',
  'venue.supportTitle': 'Ayuda 24h',
  'venue.supportCopy': 'Soporte, límites y juego responsable permanecen visibles durante toda la sesión.',
  'venue.searchPlaceholder': 'Buscar juego, salón o proveedor',
  'game.all': 'Todos',
  'game.empty': 'Aún no hay juegos disponibles.',
  'game.loadError': 'No se pudieron cargar los juegos.',
  'game.retry': 'Reintentar',
  'game.loadMore': 'Cargar más',
  'game.comingSoon': '¡Próximamente!',
  'action.audit': 'Auditoría',
  'action.deposit': 'Depositar Pix',
  'action.redeem': 'Canjear',
  'action.withdraw': 'Retirar',
  'activity.welcome.title': 'Bono de bienvenida',
  'activity.claim': 'Abrir cofre',
  'activity.claimed': 'Canjeado',
  'activity.claimSuccess': '¡Bono acreditado en tu billetera!',
  'activity.claimableNow': 'Canje disponible',
  'activity.day': 'Día',
  'activity.dayPrefix': 'Día ',
  'activity.daySuffix': '',
  'activity.details': 'Ver detalles',
  'activity.metric.deposit': 'Depósito acumulado',
  'activity.metric.signin': 'Días seguidos',
  'activity.empty': 'No hay actividades disponibles',
  'activity.toClaim': 'por canjear',
  'activity.sectionClaim': 'Para canjear',
  'activity.sectionMore': 'Más actividades',
  'activity.claimCta': 'Canjear',
  'activity.claimNeedPoints': 'Canjear cuesta {amount} puntos',
  'activity.claimPointsInsufficient': 'Puntos insuficientes, no se puede canjear',
  'activity.upTo': 'Hasta',
  'activity.unit.invites': 'invitaciones',
  'activity.cat.all': 'Todas',
  'activity.cat.daily': 'Diarias',
  'activity.cat.deposit': 'Depósito',
  'activity.cat.invite': 'Invitación',
  'activity.cat.newbie': 'Novato',
  'activity.cat.monthly': 'Mensual',
  'activity.detail.aria.conditions': 'Condiciones de la actividad',
  'activity.detail.aria.mysteryDays': 'Días de desbloqueo',
  'activity.detail.aria.overview': 'Resumen de la actividad',
  'activity.detail.chests': 'cofres',
  'activity.detail.claim.deposit': 'Depositar',
  'activity.detail.deposit.action': 'Acción',
  'activity.detail.deposit.bestTier': 'Mayor nivel del período',
  'activity.detail.deposit.bonus': 'Bono',
  'activity.detail.deposit.deposit': 'Depósito',
  'activity.detail.deposit.rollover': 'Rollover',
  'activity.detail.deposit.total': 'Total',
  'activity.detail.hero.dailySignin': 'Haz check-in todos los días, mantén la racha y abre la recompensa del día.',
  'activity.detail.hero.depositBonus': 'Deposita durante la ventana de campaña y recibe el bono del mayor nivel alcanzado.',
  'activity.detail.hero.default': 'Revisa requisitos, recompensa y reglas antes de participar.',
  'activity.detail.hero.inviteRebate': 'Invita amigos válidos, sigue tu progreso y abre cada cofre una sola vez.',
  'activity.detail.hero.memberAppreciation': 'Una recompensa mensual calculada con los depósitos del mes anterior.',
  'activity.detail.hero.newPlayerMystery': 'Los nuevos miembros desbloquean premios sorpresa en días específicos tras el registro.',
  'activity.detail.invite.directLevel': 'Nivel directo',
  'activity.detail.invite.minFriendDeposit': 'Depósito mínimo del amigo',
  'activity.detail.invite.rollover': 'Rollover del bono',
  'activity.detail.invite.validDepth': 'Profundidad válida',
  'activity.detail.member.collectionDay': 'El día 7 de cada mes',
  'activity.detail.member.collectionTime': 'Hora de cobro',
  'activity.detail.member.monthlyDepositAtLeast': 'Depósito mensual ≥',
  'activity.detail.mystery.prize': 'Premio misterioso',
  'activity.detail.overview.maxReward': 'Premio máximo',
  'activity.detail.overview.period': 'Período',
  'activity.detail.overview.rollover': 'Rollover',
  'activity.detail.period.monthPrefix': 'Período',
  'activity.detail.period.permanent': 'Permanente',
  'activity.detail.period.todayPrefix': 'Hoy',
  'activity.detail.rule.abuse': 'La participación es personal; abuso, cuenta compartida u operación de riesgo puede cancelar el bono.',
  'activity.detail.rule.dailySignin': 'El check-in es diario; si se interrumpe la racha, la próxima ronda vuelve al Día 1.',
  'activity.detail.rule.depositBonus': 'Solo el mayor nivel alcanzado en el período queda disponible para canjear.',
  'activity.detail.rule.inviteRebate': 'Cada cofre puede abrirse una vez al alcanzar la cantidad requerida de invitaciones válidas.',
  'activity.detail.rule.memberAppreciation': 'El cálculo usa depósitos exitosos del mes anterior, con cobro en el día configurado.',
  'activity.detail.rule.newPlayerMystery': 'Cada nodo de día se desbloquea según la antigüedad de la cuenta y la suma de depósitos del período.',
  'activity.detail.rule.rollover': 'El bono debe canjearse manualmente y exige rollover 1x antes del retiro.',
  'activity.detail.rolloverBonus': '1x bono',
  'activity.detail.signin.sequence': 'Racha de 7 días',
  'activity.detail.state.available': 'Disponible',
  'activity.detail.state.expired': 'Expirado',
  'activity.detail.state.ineligible': 'No disponible',
  'activity.detail.state.progress': 'En progreso',
  'activity.detail.state.visitor': 'Visitante',
  'activity.detail.status.myState': 'Mi estado',
  'activity.detail.status.nextTarget': 'Próxima meta',
  'activity.detail.targetComplete': 'Todas las etapas completadas',
  'vip.title': 'VIP',
  'vip.currentLevel': 'Nivel actual',
  'vip.maxLevel': 'Nivel máximo alcanzado',
  'vip.progressDeposit': 'Depósito acumulado',
  'vip.nextLevel': 'Próximo nivel',
  'vip.needMore': 'Faltan',
  'vip.orTurnover': 'o apuestas de',
  'vip.bonuses': 'Recompensas VIP',
  'vip.bonus.UPGRADE': 'Bono de ascenso',
  'vip.bonus.DAILY': 'Bono diario',
  'vip.bonus.WEEKLY': 'Bono semanal',
  'vip.bonus.MONTHLY': 'Bono mensual',
  'vip.claim': 'Reclamar',
  'vip.claimed': 'Reclamado',
  'vip.unavailable': 'No disponible',
  'vip.claimSuccess': '¡Recompensa recibida!',
  'vip.levelTable': 'Tabla de niveles',
  'vip.tableLevel': 'Nivel',
  'vip.tableDeposit': 'Depósito',
  'vip.tableTurnover': 'Apuestas',
  'vip.tableUpgrade': 'Ascenso',
  'vip.tableDaily': 'Diario',
  'vip.tableWeekly': 'Semanal',
  'vip.tableMonthly': 'Mensual',
  'vip.history': 'Historial',
  'vip.historyEmpty': 'Sin reclamos todavía',
  'vip.loginPrompt': 'Inicia sesión para ver tu nivel VIP y reclamar recompensas.',
  'vip.loadError': 'No se pudieron cargar los datos VIP.',
  'vip.retry': 'Reintentar',
  'activity.invite.copied': '¡Enlace copiado!',
  'activity.invite.copy': 'Copiar enlace de invitación',
  'activity.invite.title': 'Mi código de invitación',
  'activity.loadError': 'No se pudieron cargar las actividades.',
  'activity.loading': 'Cargando actividades...',
  'activity.loginPrompt': 'Inicia sesión para ver las actividades y recompensas.',
  'activity.locked': 'Bloqueado',
  'activity.loginToJoin': 'Inicia sesión para participar',
  'activity.progress': 'Invitaciones válidas',
  'activity.retry': 'Reintentar',
  'activity.rules': 'Reglas',
  'activity.tiers': 'Cofres de recompensa',
  'agent.collectable': 'Cobrable:',
  'agent.comingSoon': 'Próximamente en esta versión.',
  'agent.copy': 'Copiar',
  'agent.noCommission': 'No hay comisión disponible para cobrar',
  'agent.open': 'Abrir',
  'agent.qrSaved': 'QR guardado',
  'agent.qrUnavailable': 'QR no disponible',
  'agent.share': 'Compartir',
  'agent.commission': 'Comisión',
  'agent.commissionLast': 'Última comisión recibida',
  'agent.commissionReceived': 'Comisión recibida',
  'agent.commissionTotal': 'Comisión total',
  'agent.directPerf': 'Desempeño directo',
  'agent.directSubs': 'Subordinados directos',
  'agent.history': 'Historial',
  'agent.linkCopied': 'Enlace copiado',
  'agent.more': 'más',
  'agent.myId': 'Mi ID:',
  'agent.myLink': 'Mi enlace',
  'agent.otherMembers': 'Otros miembros',
  'agent.otherPerf': 'Otro desempeño',
  'agent.people': 'Personas',
  'agent.performance': 'Desempeño',
  'agent.receive': 'Recibir',
  'agent.refInfo': 'Información de referido',
  'agent.referrerId': 'ID del invitador:',
  'agent.save': 'Guardar',
  'agent.settleNote': 'La comisión se liquida todos los días de 4:00 a 8:00.',
  'agent.tab.commission': 'Comisión',
  'agent.tab.link': 'Enlace de invitación',
  'agent.tab.network': 'Red de agentes',
  'agent.tab.performance': 'Desempeño',
  'agent.tab.rate': 'Tasa de bono',
  'agent.tab.register': 'Registrar subordinados',
  'agent.teamTotal': 'Total de miembros del equipo',
  'agent.totalRevenue': 'Ingreso total',
  'agent.records.empty': 'Sin registros',
  'agent.net.intro': 'Modelo de distribución multinivel: gana comisión directa de tus referidos y comisión por diferencia de nivel de toda la red debajo de ti.',
  'agent.net.directTeam': 'Equipo directo',
  'agent.net.directTeamDesc': 'Miembros que invitaste directamente (nivel 1).',
  'agent.net.otherTeam': 'Otros equipos',
  'agent.net.otherTeamDesc': 'Referidos de tus referidos (nivel 2 en adelante, sin límite).',
  'agent.net.formula': 'Comisión = Directa 3% + Diferencia (tu tasa − tasa del downline). Diferencia ≤ 0 no genera comisión.',
  'agent.net.teamPerf': 'Desempeño total del equipo',
  'agent.net.totalComm': 'Comisión total',
  'agent.net.directPerf': 'Desempeño directo',
  'agent.net.directComm': 'Comisión directa (3%)',
  'agent.net.otherPerf': 'Otro desempeño',
  'agent.net.otherComm': 'Comisión de nivel (2%)',
  'agent.net.example': 'Ejemplo oficial',
  'agent.net.exampleNote': 'A invita a B1/B2/B3; B1 invita a C1/C2; B3 invita a C3. Apuestas válidas al día siguiente: B1=500, B2=3.000, B3=2.000, C1=1.000, C2=2.000, C3=20.000. B1 gana 30, B3 gana 600; total de A = 165 (directa) + 60 (nivel) = 225.',
  'agent.col.id': 'ID',
  'agent.col.subordinates': 'Subordinados',
  'agent.col.bet': 'Apuesta',
  'agent.col.performance': 'Desempeño',
  'agent.col.deposit': 'Depósito',
  'agent.col.time': 'Tiempo',
  'agent.col.type': 'Tipo',
  'agent.col.people': 'Personas',
  'agent.col.commission': 'Comisión',
  'agent.filter.searchId': 'Buscar por ID',
  'agent.filter.all': 'Todos',
  'agent.commType.direct': 'Directa',
  'agent.commType.diff': 'Nivel',
  'agent.rate.level': 'Nivel',
  'agent.rate.threshold': 'Desempeño ≥',
  'agent.rate.value': 'Tasa',
  'agent.rate.cat.slot': 'Slot',
  'agent.rate.cat.cartas': 'Cartas',
  'agent.rate.cat.pesca': 'Pesca',
  'agent.rate.cat.cassino': 'Casino',
  'agent.rate.pending': 'Tabla en configuración.',
  'agent.reg.note': 'Completa la información a continuación para terminar de abrir la cuenta.',
  'agent.reg.phone': 'Teléfono',
  'agent.reg.password': 'Contraseña',
  'agent.reg.confirm': 'Confirmar contraseña',
  'agent.reg.submit': 'Crear',
  'agent.reg.success': 'Cuenta del subordinado creada',
  'agent.reg.mismatch': 'Las contraseñas no coinciden',
  'agent.reg.invalidPhone': 'Teléfono inválido',
  'agent.reg.weakPwd': 'La contraseña debe tener al menos 6 caracteres',
  'app.offlineStable': 'Config local',
  'auth.action.clear': 'Borrar',
  'auth.action.close': 'Cerrar',
  'auth.action.hasAccount': '¿Ya tienes una cuenta?',
  'auth.action.haveAccount': 'Ya tengo cuenta',
  'auth.action.hide': 'Ocultar',
  'auth.action.noAccount': '¿No tienes una cuenta?',
  'auth.action.recover': '¿Olvidaste la contraseña?',
  'auth.action.show': 'Mostrar',
  'auth.error.displayName': 'El nombre visible debe tener como máximo 96 caracteres.',
  'auth.error.loginName': 'Número de teléfono inválido',
  'auth.error.password': '6-16 caracteres',
  'auth.error.passwordMismatch': 'Las contraseñas no coinciden.',
  'auth.field.confirmPassword': 'Confirmar contraseña',
  'auth.field.displayName': 'Nombre visible',
  'auth.field.displayNamePlaceholder': 'Tu nombre en la app',
  'auth.field.loginName': 'Teléfono',
  'auth.field.password': 'Contraseña',
  'auth.login.heading': 'Inicia sesión en tu cuenta',
  'auth.login.submit': 'Entrar',
  'auth.login.submitting': 'Entrando...',
  'auth.login.subtitle': 'Usa celular, usuario o correo para acceder.',
  'auth.error.age': 'Confirma que tienes 18 años o más.',
  'auth.field.referralCode': 'Código de invitación',
  'auth.option.age': 'Tengo 18 años o más',
  'auth.option.remember': 'Recordar contraseña',
  'auth.referralApplied': 'Invitación aplicada:',
  'auth.register.heading': 'Crea una cuenta de juego',
  'auth.register.rewardTag': '+R$56',
  'auth.register.submit': 'Registro',
  'auth.register.submitting': 'Registrando...',
  'auth.register.subtitle': 'Crea tu cuenta con celular, usuario o correo.',
  'auth.security.sms': 'SMS protegido para recuperar el acceso',
  'auth.sessionExpired': 'Tu sesión ha expirado. Inicia sesión de nuevo.',
  'auth.serverError.ipDenied': 'El inicio de sesión desde esta IP está restringido. Contacta con atención al cliente.',
  'auth.serverError.accountDisabled': 'Esta cuenta está deshabilitada. Contacta con atención al cliente.',
  'auth.serverError.invalidCredentials': 'Celular o contraseña incorrectos. Verifica e inténtalo de nuevo.',
  'auth.serverError.smsCodeInvalid': 'El código de verificación es incorrecto. Revísalo e inténtalo de nuevo.',
  'auth.serverError.passwordInvalid': 'Contraseña incorrecta. Verifica e inténtalo de nuevo.',
  'auth.serverError.userNotFound': 'Esta cuenta no existe. Regístrate para continuar.',
  'auth.serverError.loginFailed': 'No fue posible entrar ahora.',
  'auth.serverError.loginValidation': 'Ingresa una cuenta válida y una contraseña de al menos 6 caracteres.',
  'auth.serverError.rateLimited': 'Demasiados intentos. Espera un momento e inténtalo de nuevo.',
  'auth.serverError.registerFailed': 'No fue posible crear la cuenta ahora.',
  'auth.serverError.registerValidation': 'La cuenta o el teléfono ya existe. Inicia sesión.',
  'auth.serverError.tenantScope': 'Este sitio no está disponible para iniciar sesión ahora.',
  'auth.serverError.unavailable': 'Servicio de autenticación no disponible. Inténtalo de nuevo en unos instantes.',
  'auth.tab.login': 'Entrar',
  'auth.tab.register': 'Registro',
  'auth.tabsLabel': 'Acceso',
  'auth.trust.licensed': 'Con licencia',
  'auth.trust.licensedValue': 'SPA/MF',
  'auth.trust.phone': 'Celular',
  'auth.trust.phoneValue': 'SMS protegido',
  'auth.trust.safe': 'Juego seguro',
  'auth.trust.safeValue': 'Límites activos',
  'campaign.deposit-wed.cta': 'Depositar ahora',
  'campaign.deposit-wed.eyebrow': 'Miércoles de juego',
  'campaign.deposit-wed.reward': 'Hasta R$ 100',
  'campaign.deposit-wed.subtitle': 'Deposita el miércoles y gana un bono extra.',
  'campaign.deposit-wed.title': 'Bono de depósito',
  'campaign.member-day.cta': 'Ver recompensa',
  'campaign.member-day.eyebrow': 'Cada día 7',
  'campaign.member-day.reward': 'Hasta R$ 100',
  'campaign.member-day.subtitle': 'Recompensa por tus depósitos del mes pasado.',
  'campaign.member-day.title': 'Apreciación a miembros',
  'campaign.mystery-newbie.cta': 'Abrir premio',
  'campaign.mystery-newbie.eyebrow': 'Bono de novato',
  'campaign.mystery-newbie.reward': 'Hasta R$ 888',
  'campaign.mystery-newbie.subtitle': 'Deposita en tus primeros días y abre premios sorpresa.',
  'campaign.mystery-newbie.title': 'Premio misterioso',
  'campaign.weekend-pix.cta': 'Depositar con Pix',
  'campaign.weekend-pix.eyebrow': 'Fin de semana',
  'campaign.weekend-pix.reward': 'Hasta R$ 500',
  'campaign.weekend-pix.subtitle': 'Fútbol en vivo, casino y 50 giros gratis.',
  'campaign.weekend-pix.title': 'Juego y diversión',
  'common.active': 'Activo',
  'common.pending': 'Pendiente',
  'common.seeAll': 'Ver todo',
  'common.unavailable': 'No disponible',
  'common.verified': 'Verificado',
  'compliance.license.br': 'Licenciado para operación regulada. Juega con responsabilidad.',
  'compliance.license.mx': 'Operación para México. Juega con responsabilidad. +18.',
  'compliance.responsibleGaming.entry': 'Juego responsable',
  'deposit.mxSkeletonHint': 'La réplica 1:1 de Oro Casino está pendiente. Este panel usa MXN y el canal SIMULATE.',
  'deposit.mxSkeletonTitle': 'Depósito MXN',
  'compliance.strip.licensed': 'Licenciado',
  'compliance.strip.pix': 'Pix activo',
  'compliance.strip.cpf': 'CPF verificado',
  'compliance.strip.responsible': 'Juego responsable',
  'deposit.title': 'Depositar con Pix',
  'games.title': 'Juegos',
  'home.title': 'Inicio',
  'home.carousel.aria': 'Promociones destacadas',
  'home.carousel.select': 'Seleccionar promoción',
  'home.preview.cta': 'Entrar ahora',
  'home.preview.eyebrow': 'Destacado',
  'home.preview.title': 'Promoción',
  'home.preview.endsIn': 'Termina en',
  'home.venues.title': 'Salones de juego',
  'home.venues.aria': 'Salones de juego',
  'home.venues.unit': 'juegos',
  'home.announce.aria': 'Avisos y novedades',
  'announce.deposit': 'Usuario {id} recargó {amount}',
  'home.announce.payout': 'Retiros vía Pix procesados en hasta 3 minutos.',
  'home.announce.bonus': '100% de bono en tu primer depósito, hasta R$ 500.',
  'home.announce.draw': 'Sorteo de la suerte a las 21h: R$ 10.000 en premios.',
  'home.slots.title': 'Slots populares',
  'language.title': 'Idioma',
  'nav.account': 'Mi cuenta',
  'nav.activities': 'Actividades',
  'nav.agent': 'Agente',
  'agent.centerAria': 'Centro de agente',
  'nav.casino': 'Casino',
  'nav.deposit': 'PIX',
  'nav.games': 'Deportes',
  'nav.home': 'Inicio',
  'nav.venue': 'Salones',
  'profile.account': 'Cuenta',
  'profile.edit': 'Editar perfil',
  'profile.nickname': 'Apodo',
  'profile.pickAvatar': 'Elige un avatar',
  'profile.save': 'Guardar',
  'profile.title': 'Mis datos',
  'wallet.balance': 'Saldo disponible',
  'wallet.refresh': 'Actualizar saldo',
  'wallet.history': 'Historial',
  'wallet.playReal': 'Juega con saldo real',
  'wallet.quickActions': 'Acciones rápidas',
  'wallet.realBalance': 'Saldo real',
  'wallet.recentTx': 'Últimas transacciones',
  'wallet.released': 'Saldo liberado para retiro',
  'wallet.rolloverPending': 'Rollover pendiente',
  'wallet.title': 'Cartera',
  'wallet.loading': 'Cargando transacciones...',
  'wallet.empty': 'Aún no hay transacciones',
  'wallet.loadError': 'No se pudieron cargar las transacciones.',
  'wallet.retry': 'Reintentar',
  'wallet.loginRequired': 'Inicia sesión para ver tu historial',
};

const zhCN: Dictionary = {
  'account.login': '登录',
  'account.register': '注册',
  'account.logout': '退出登录',
  'account.cpf': 'CPF',
  'account.dailyLimit': '每日限额',
  'account.pix': 'Pix',
  'account.statusPending': 'CPF 待验证',
  'account.statusVerified': 'CPF 已验证 · Pix 已开通',
  'account.title': '账户',
  'account.txStatus.done': '已完成',
  'account.txStatus.review': '审核中',
  'account.txStatus.available': '可用',
  'account.tx.depositPix': 'Pix 充值',
  'account.tx.withdrawPix': 'Pix 提现',
  'account.tx.bonusReleased': '奖金已发放',
  'account.tx.depositDetail': 'CPF 已验证 · 10:24',
  'account.tx.withdrawDetail': '本地银行 · CPF',
  'account.tx.bonusDetail': '返水 15%',
  'account.walletSecurityAria': '钱包安全概览',
  'account.quickActionsAria': '钱包快捷操作',
  'account.manage': '账户管理',
  'account.txHistory': '交易历史',
  'account.toNextVip': '距离 VIP',
  'account.turnoverLeft': '还需打码',
  'account.maxVip': '已达最高等级',
  'common.available': '可用',
  'common.loading': '加载中…',
  'common.back': '返回',
  'common.search': '搜索',
  'common.filter': '筛选',
  'common.between': '介于',
  'common.and': '到',
  'common.chars': '个字符',
  'deposit.fastTitle': 'Pix 秒到账',
  'deposit.fastDesc': '持卡人 CPF 必须与账户一致。',
  'deposit.chooseAmount': '选择金额',
  'deposit.quickAria': '快捷金额',
  'deposit.other': '其他',
  'deposit.cpfHolder': '持卡人 CPF',
  'deposit.method': '方式',
  'deposit.methodValue': 'Pix 复制粘贴 / 二维码',
  'deposit.qrAlt': '已生成 Pix 二维码',
  'deposit.qrReady': '已生成 Pix 二维码',
  'deposit.expiresIn': '剩余',
  'deposit.copyCode': '复制 Pix 代码',
  'deposit.bonusTitle': '充值奖金 15%',
  'deposit.bonusDesc': '仅限当日首笔 Pix。',
  'redeem.title': '兑换码',
  'redeem.intro': '输入你的促销码，将奖金记入钱包。',
  'redeem.codeLabel': '兑换码',
  'redeem.placeholder': '例如：RC-XXXXXXXXXX',
  'redeem.submit': '兑换',
  'redeem.submitting': '兑换中...',
  'redeem.bonusOf': '奖金',
  'redeem.credited': '已到账！',
  'redeem.rollover': '所需流水：',
  'redeem.seeAudit': '查看稽核',
  'redeem.errClaimed': '你已兑换过该兑换码。',
  'redeem.errExhausted': '该兑换码已用尽。',
  'redeem.errExpired': '兑换码已过期或尚未生效。',
  'redeem.errInvalid': '兑换码无效。',
  'redeem.errIpLimited': '该网络兑换次数已达上限。',
  'redeem.errDeviceLimited': '该设备兑换次数已达上限。',
  'redeem.errGeneric': '兑换失败，请重试。',
  'audit.title': '稽核记录',
  'audit.rolloverDone': '流水已完成 — 提现已解锁',
  'audit.completeHint': '完成流水后即可解锁提现。',
  'audit.empty': '暂无稽核记录。',
  'audit.remaining': '还差',
  'audit.status.cancelled': '已取消',
  'audit.status.completed': '已完成',
  'audit.status.expired': '已过期',
  'audit.status.inProgress': '进行中',
  'audit.status.pending': '待处理',
  'profile.avatarsAria': '可选头像',
  'profile.namePlaceholder': '你的昵称',
  'profile.saved': '已保存',
  'profile.errSave': '保存失败，请检查信息后重试。',
  'profile.errNetwork': '连接失败，请重试。',
  'shell.backHome': '返回首页',
  'shell.selectCountry': '选择国家',
  'shell.country': '巴西',
  'shell.openWallet': '打开钱包',
  'shell.openAccount': '打开账户，18+',
  'shell.mainNav': '主导航',
  'shell.booting': '正在加载配置',
  'shell.bootingDesc': '正在准备主题、语言与站点规则。',
  'shell.unavailable': '应用不可用',
  'compliance.strip.aria': '合规与安全信息',
  'kyc.panelTitle': 'CPF 验证',
  'kyc.panelCopy': '完成 CPF 与年龄验证，解锁充值、提现与权益。',
  'kyc.panelCta': '继续验证',
  'kyc.supportTitle': 'KYC 帮助',
  'kyc.supportCopy': '验证可保护余额、Pix 提现与国家规则。如有疑问，请联系客服。',
  'kyc.required': '必填',
  'kyc.reqAria': '验证要求',
  'kyc.cpfLabel': 'CPF 必填',
  'kyc.cpfValue': '有效证件',
  'kyc.ageLabel': '18+',
  'kyc.ageValue': '年龄已确认',
  'kyc.regionLabel': '巴西',
  'kyc.regionValue': '本地账户',
  'promo.heroTitle': '促销活动',
  'promo.heroSubtitle': '条款、流水与期限需来自已发布的内容。',
  'promo.heroBadge': '限时优惠',
  'promo.heroCta': '参与',
  'promo.actionClaim': '参与',
  'promo.actionRules': '查看规则',
  'promo.actionVenue': '进入大厅',
  'promo.supportTitle': '条款与客服',
  'promo.supportCopy': '活动取决于登录、CPF/KYC 以及为该国家发布的规则。',
  'promo.taskTitle': '每日任务',
  'promo.taskCopy': '参与状态将由活动 API 加载。',
  'promo.taskCta': '即将上线',
  'promo.actionsAria': '促销操作',
  'venue.heroTitle': '真人娱乐场',
  'venue.heroSubtitle': '实时呈现桌台、限额与供应商状态。',
  'venue.heroBadge': 'Live',
  'venue.heroCta': '进入大厅',
  'venue.supportTitle': '24 小时帮助',
  'venue.supportCopy': '客服、限额与责任博彩在整个会话中保持可见。',
  'venue.searchPlaceholder': '搜索游戏、大厅或供应商',
  'game.all': '全部',
  'game.empty': '暂无可用游戏。',
  'game.loadError': '游戏加载失败。',
  'game.retry': '重试',
  'game.loadMore': '加载更多',
  'game.comingSoon': '即将开放！',
  'action.audit': '稽核',
  'action.deposit': 'Pix 充值',
  'action.redeem': '兑换',
  'action.withdraw': '提现',
  'activity.welcome.title': '欢迎奖金',
  'activity.claim': '开宝箱',
  'activity.claimed': '已领取',
  'activity.claimSuccess': '奖励已入账钱包！',
  'activity.claimableNow': '可领取',
  'activity.day': '第',
  'activity.dayPrefix': '第 ',
  'activity.daySuffix': ' 天',
  'activity.details': '查看详情',
  'activity.metric.deposit': '累计充值',
  'activity.metric.signin': '连续签到',
  'activity.empty': '暂无可参与的活动',
  'activity.toClaim': '待领取',
  'activity.sectionClaim': '可领取',
  'activity.sectionMore': '更多活动',
  'activity.claimCta': '领取',
  'activity.claimNeedPoints': '领取需要积分 {amount}',
  'activity.claimPointsInsufficient': '积分不足，无法领取',
  'activity.upTo': '最高',
  'activity.unit.invites': '人',
  'activity.cat.all': '全部',
  'activity.cat.daily': '每日',
  'activity.cat.deposit': '充值',
  'activity.cat.invite': '邀请',
  'activity.cat.newbie': '新人',
  'activity.cat.monthly': '每月',
  'activity.detail.aria.conditions': '活动条件',
  'activity.detail.aria.mysteryDays': '解锁日期',
  'activity.detail.aria.overview': '活动摘要',
  'activity.detail.chests': '个宝箱',
  'activity.detail.claim.deposit': '去充值',
  'activity.detail.deposit.action': '操作',
  'activity.detail.deposit.bestTier': '本周期最高档位',
  'activity.detail.deposit.bonus': '奖金',
  'activity.detail.deposit.deposit': '充值',
  'activity.detail.deposit.rollover': '打码',
  'activity.detail.deposit.total': '合计',
  'activity.detail.hero.dailySignin': '每天签到，保持连续天数，开启当天奖励。',
  'activity.detail.hero.depositBonus': '在活动周期内充值，领取已达最高档位的奖金。',
  'activity.detail.hero.default': '参与前请查看条件、奖励和活动规则。',
  'activity.detail.hero.inviteRebate': '邀请有效好友，查看进度，每个宝箱仅可开启一次。',
  'activity.detail.hero.memberAppreciation': '按上月充值计算的每月会员回馈奖励。',
  'activity.detail.hero.newPlayerMystery': '新会员注册后在指定天数解锁神秘奖励。',
  'activity.detail.invite.directLevel': '直属好友',
  'activity.detail.invite.minFriendDeposit': '好友最低充值',
  'activity.detail.invite.rollover': '奖金打码',
  'activity.detail.invite.validDepth': '有效层级',
  'activity.detail.member.collectionDay': '每月 7 日领取',
  'activity.detail.member.collectionTime': '领取时间',
  'activity.detail.member.monthlyDepositAtLeast': '月充值 ≥',
  'activity.detail.mystery.prize': '神秘奖励',
  'activity.detail.overview.maxReward': '最高奖励',
  'activity.detail.overview.period': '周期',
  'activity.detail.overview.rollover': '打码',
  'activity.detail.period.monthPrefix': '周期',
  'activity.detail.period.permanent': '长期有效',
  'activity.detail.period.todayPrefix': '今日',
  'activity.detail.rule.abuse': '活动仅限本人参与；滥用、共享账号或风险操作可能导致奖励取消。',
  'activity.detail.rule.dailySignin': '签到按日计算；连续签到中断后，下一轮将从第 1 天重新开始。',
  'activity.detail.rule.depositBonus': '活动周期内仅可领取已达成的最高档位奖励。',
  'activity.detail.rule.inviteRebate': '达到有效邀请人数后，每个宝箱可开启一次。',
  'activity.detail.rule.memberAppreciation': '奖励按上月成功充值计算，并在配置的领取日开放领取。',
  'activity.detail.rule.newPlayerMystery': '每个日期节点按账号注册天数和周期内累计充值解锁。',
  'activity.detail.rule.rollover': '奖励需手动领取，提现前需完成 1 倍打码。',
  'activity.detail.rolloverBonus': '1 倍奖金',
  'activity.detail.signin.sequence': '连续 7 天签到',
  'activity.detail.state.available': '可领取',
  'activity.detail.state.expired': '已过期',
  'activity.detail.state.ineligible': '不可参与',
  'activity.detail.state.progress': '进行中',
  'activity.detail.state.visitor': '游客',
  'activity.detail.status.myState': '我的状态',
  'activity.detail.status.nextTarget': '下一目标',
  'activity.detail.targetComplete': '所有阶段已完成',
  'vip.title': 'VIP',
  'vip.currentLevel': '当前等级',
  'vip.maxLevel': '已达最高等级',
  'vip.progressDeposit': '累计充值',
  'vip.nextLevel': '下一等级',
  'vip.needMore': '还差',
  'vip.orTurnover': '或投注',
  'vip.bonuses': 'VIP 奖励',
  'vip.bonus.UPGRADE': '升级礼金',
  'vip.bonus.DAILY': '每日奖',
  'vip.bonus.WEEKLY': '每周奖',
  'vip.bonus.MONTHLY': '每月奖',
  'vip.claim': '领取',
  'vip.claimed': '已领取',
  'vip.unavailable': '不可领取',
  'vip.claimSuccess': '领取成功！',
  'vip.levelTable': '等级表',
  'vip.tableLevel': '等级',
  'vip.tableDeposit': '充值',
  'vip.tableTurnover': '投注',
  'vip.tableUpgrade': '升级',
  'vip.tableDaily': '日奖',
  'vip.tableWeekly': '周奖',
  'vip.tableMonthly': '月奖',
  'vip.history': '领取记录',
  'vip.historyEmpty': '暂无领取记录',
  'vip.loginPrompt': '登录后查看 VIP 等级并领取奖励。',
  'vip.loadError': '加载 VIP 数据失败。',
  'vip.retry': '重试',
  'activity.invite.copied': '链接已复制！',
  'activity.invite.copy': '复制邀请链接',
  'activity.invite.title': '我的邀请码',
  'activity.loadError': '活动加载失败。',
  'activity.loading': '活动加载中...',
  'activity.loginPrompt': '登录后查看活动与奖励。',
  'activity.locked': '未解锁',
  'activity.loginToJoin': '登录后参与领取',
  'activity.progress': '有效邀请',
  'activity.retry': '重试',
  'activity.rules': '活动规则',
  'activity.tiers': '奖励宝箱',
  'agent.collectable': '可领取：',
  'agent.comingSoon': '该功能敬请期待。',
  'agent.copy': '复制',
  'agent.noCommission': '暂无可领取佣金',
  'agent.open': '打开',
  'agent.qrSaved': '二维码已保存',
  'agent.qrUnavailable': '二维码不可用',
  'agent.share': '分享',
  'agent.commission': '佣金',
  'agent.commissionLast': '上次到账佣金',
  'agent.commissionReceived': '已领佣金',
  'agent.commissionTotal': '佣金总额',
  'agent.directPerf': '直属业绩',
  'agent.directSubs': '直属下级',
  'agent.history': '历史',
  'agent.linkCopied': '链接已复制',
  'agent.more': '更多',
  'agent.myId': '我的ID：',
  'agent.myLink': '我的链接',
  'agent.otherMembers': '其他成员',
  'agent.otherPerf': '其他业绩',
  'agent.people': '人',
  'agent.performance': '业绩',
  'agent.receive': '领取',
  'agent.refInfo': '推广信息',
  'agent.referrerId': '邀请人ID：',
  'agent.save': '保存',
  'agent.settleNote': '佣金结算时间为每天 4:00-8:00。',
  'agent.tab.commission': '佣金',
  'agent.tab.link': '邀请链接',
  'agent.tab.network': '代理网络',
  'agent.tab.performance': '业绩',
  'agent.tab.rate': '奖金费率',
  'agent.tab.register': '注册下级',
  'agent.teamTotal': '团队成员总数',
  'agent.totalRevenue': '总收益',
  'agent.records.empty': '暂无记录',
  'agent.net.intro': '多级分销模式：从直属下级获得直推佣金，并从其下整条网络获得级差佣金。',
  'agent.net.directTeam': '直属团队',
  'agent.net.directTeamDesc': '由你直接邀请的成员（第 1 级）。',
  'agent.net.otherTeam': '其他团队',
  'agent.net.otherTeamDesc': '下级的下级（第 2 级及以下，无限层级）。',
  'agent.net.formula': '佣金 = 直推 3% + 级差（自身费率 − 下级费率）。级差 ≤ 0 不产生佣金。',
  'agent.net.teamPerf': '团队总业绩',
  'agent.net.totalComm': '总佣金',
  'agent.net.directPerf': '直推业绩',
  'agent.net.directComm': '直推佣金（3%）',
  'agent.net.otherPerf': '其他业绩',
  'agent.net.otherComm': '级差佣金（2%）',
  'agent.net.example': '官方算例',
  'agent.net.exampleNote': 'A 发展 B1/B2/B3；B1 发展 C1/C2；B3 发展 C3。次日有效投注：B1=500、B2=3.000、B3=2.000、C1=1.000、C2=2.000、C3=20.000。B1 佣金=30、B3 佣金=600；A 总佣金 = 165（直推）+ 60（级差）= 225。',
  'agent.col.id': 'ID',
  'agent.col.subordinates': '下级数',
  'agent.col.bet': '投注',
  'agent.col.performance': '业绩',
  'agent.col.deposit': '充值',
  'agent.col.time': '时间',
  'agent.col.type': '类型',
  'agent.col.people': '人数',
  'agent.col.commission': '佣金',
  'agent.filter.searchId': '按 ID 搜索',
  'agent.filter.all': '全部',
  'agent.commType.direct': '直推',
  'agent.commType.diff': '级差',
  'agent.rate.level': '等级',
  'agent.rate.threshold': '业绩 ≥',
  'agent.rate.value': '费率',
  'agent.rate.cat.slot': '老虎机',
  'agent.rate.cat.cartas': '棋牌',
  'agent.rate.cat.pesca': '捕鱼',
  'agent.rate.cat.cassino': '真人',
  'agent.rate.pending': '费率表配置中。',
  'agent.reg.note': '请填写以下信息以完成开户。',
  'agent.reg.phone': '手机号',
  'agent.reg.password': '密码',
  'agent.reg.confirm': '确认密码',
  'agent.reg.submit': '创建',
  'agent.reg.success': '下级账号已创建',
  'agent.reg.mismatch': '两次密码不一致',
  'agent.reg.invalidPhone': '手机号无效',
  'agent.reg.weakPwd': '密码至少 6 位',
  'app.offlineStable': '本地配置',
  'auth.action.clear': '清除',
  'auth.action.close': '关闭',
  'auth.action.hasAccount': '已有账号？',
  'auth.action.haveAccount': '已有账号',
  'auth.action.hide': '隐藏',
  'auth.action.noAccount': '还没有账号？',
  'auth.action.recover': '忘记密码？',
  'auth.action.show': '显示',
  'auth.error.displayName': '昵称最多 96 个字符。',
  'auth.error.loginName': '手机号无效',
  'auth.error.password': '6-16 个字符',
  'auth.error.passwordMismatch': '两次输入的密码不一致。',
  'auth.field.confirmPassword': '确认密码',
  'auth.field.displayName': '昵称',
  'auth.field.displayNamePlaceholder': '在应用中显示的名称',
  'auth.field.loginName': '手机号',
  'auth.field.password': '密码',
  'auth.login.heading': '登录你的账号',
  'auth.login.submit': '登录',
  'auth.login.submitting': '登录中...',
  'auth.login.subtitle': '使用手机号、用户名或邮箱登录。',
  'auth.error.age': '请确认您已年满 18 岁。',
  'auth.field.referralCode': '邀请码',
  'auth.option.age': '我已年满 18 岁',
  'auth.option.remember': '记住密码',
  'auth.referralApplied': '已应用邀请码：',
  'auth.register.heading': '创建游戏账号',
  'auth.register.rewardTag': '+R$56',
  'auth.register.submit': '注册',
  'auth.register.submitting': '注册中...',
  'auth.register.subtitle': '使用手机号、用户名或邮箱创建账号。',
  'auth.security.sms': '短信验证保护账号找回',
  'auth.sessionExpired': '登录已过期，请重新登录',
  'auth.serverError.ipDenied': '当前 IP 已被限制登录，请联系客服。',
  'auth.serverError.accountDisabled': '账号已被禁用，请联系客服。',
  'auth.serverError.invalidCredentials': '手机号或密码错误，请检查后重试。',
  'auth.serverError.smsCodeInvalid': '验证码错误，请检查后重试。',
  'auth.serverError.passwordInvalid': '密码错误，请检查后重试。',
  'auth.serverError.userNotFound': '账号不存在，请先注册。',
  'auth.serverError.loginFailed': '暂时无法登录。',
  'auth.serverError.loginValidation': '请输入有效账号和至少 6 位的密码。',
  'auth.serverError.rateLimited': '尝试次数过多，请稍后再试。',
  'auth.serverError.registerFailed': '暂时无法创建账号。',
  'auth.serverError.registerValidation': '账号或手机号已存在，请直接登录。',
  'auth.serverError.tenantScope': '该站点暂时无法登录。',
  'auth.serverError.unavailable': '认证服务暂不可用，请稍后重试。',
  'auth.tab.login': '登录',
  'auth.tab.register': '注册',
  'auth.tabsLabel': '登录入口',
  'auth.trust.licensed': '持牌运营',
  'auth.trust.licensedValue': 'SPA/MF',
  'auth.trust.phone': '手机',
  'auth.trust.phoneValue': '短信保护',
  'auth.trust.safe': '安全游戏',
  'auth.trust.safeValue': '已启用限额',
  'campaign.deposit-wed.cta': '立即充值',
  'campaign.deposit-wed.eyebrow': '周三活动日',
  'campaign.deposit-wed.reward': '最高 R$ 100',
  'campaign.deposit-wed.subtitle': '周三充值享额外返奖。',
  'campaign.deposit-wed.title': '充值返奖',
  'campaign.member-day.cta': '查看奖励',
  'campaign.member-day.eyebrow': '每月 7 号',
  'campaign.member-day.reward': '最高 R$ 100',
  'campaign.member-day.subtitle': '按上月充值送感恩奖励。',
  'campaign.member-day.title': '会员感恩日',
  'campaign.mystery-newbie.cta': '开启奖励',
  'campaign.mystery-newbie.eyebrow': '新人福利',
  'campaign.mystery-newbie.reward': '最高 R$ 888',
  'campaign.mystery-newbie.subtitle': '注册前几天充值，开出随机神秘奖。',
  'campaign.mystery-newbie.title': '神秘奖励',
  'campaign.weekend-pix.cta': 'Pix 充值',
  'campaign.weekend-pix.eyebrow': '周末',
  'campaign.weekend-pix.reward': '最高 R$ 500',
  'campaign.weekend-pix.subtitle': '足球直播、真人娱乐场与 50 次免费旋转。',
  'campaign.weekend-pix.title': '畅玩有乐趣',
  'common.active': '已开通',
  'common.pending': '待处理',
  'common.seeAll': '查看全部',
  'common.unavailable': '不可用',
  'common.verified': '已验证',
  'compliance.license.br': '持牌合规运营，请理性博彩。',
  'compliance.license.mx': '墨西哥站运营，请理性博彩。18+。',
  'compliance.responsibleGaming.entry': '责任博彩',
  'deposit.mxSkeletonHint': 'Oro Casino 1:1 皮肤待源站抓取。本页使用 MXN 与 SIMULATE 通道。',
  'deposit.mxSkeletonTitle': 'MXN 充值',
  'compliance.strip.licensed': '持牌合规',
  'compliance.strip.pix': 'Pix 已启用',
  'compliance.strip.cpf': 'CPF 已验证',
  'compliance.strip.responsible': '责任博彩',
  'deposit.title': 'Pix 充值',
  'games.title': '游戏',
  'home.title': '首页',
  'home.carousel.aria': '精选活动',
  'home.carousel.select': '选择活动',
  'home.preview.cta': '立即进入',
  'home.preview.eyebrow': '精选',
  'home.preview.title': '促销活动',
  'home.preview.endsIn': '倒计时',
  'home.venues.title': '游戏大厅',
  'home.venues.aria': '游戏大厅',
  'home.venues.unit': '款',
  'home.announce.aria': '公告与资讯',
  'announce.deposit': '玩家 {id} 充值了 {amount}',
  'home.announce.payout': 'Pix 提现 3 分钟内到账。',
  'home.announce.bonus': '首存赠送 100% 奖金，最高 R$ 500。',
  'home.announce.draw': '晚 21 点幸运抽奖：R$ 10.000 奖池。',
  'home.slots.title': '热门老虎机',
  'language.title': '语言',
  'nav.account': '我的',
  'nav.activities': '活动',
  'nav.agent': '代理',
  'agent.centerAria': '代理中心',
  'nav.casino': '娱乐场',
  'nav.deposit': 'PIX',
  'nav.games': '体育',
  'nav.home': '首页',
  'nav.venue': '场馆',
  'profile.account': '账户',
  'profile.edit': '编辑资料',
  'profile.nickname': '昵称',
  'profile.pickAvatar': '选择头像',
  'profile.save': '保存',
  'profile.title': '我的资料',
  'wallet.balance': '可用余额',
  'wallet.refresh': '刷新余额',
  'wallet.history': '历史',
  'wallet.playReal': '用真实余额游戏',
  'wallet.quickActions': '快捷操作',
  'wallet.realBalance': '真实余额',
  'wallet.recentTx': '最近交易',
  'wallet.released': '余额可提现',
  'wallet.rolloverPending': '待完成打码',
  'wallet.title': '钱包',
  'wallet.loading': '加载交易中...',
  'wallet.empty': '暂无交易记录',
  'wallet.loadError': '交易加载失败',
  'wallet.retry': '重试',
  'wallet.loginRequired': '登录后查看交易历史',
};

const catalogs: Record<LocaleCode, Dictionary> = {
  'en-US': enUS,
  'es-ES': { ...esES },
  'es-MX': { ...esES },
  'pt-BR': ptBR,
  'zh-CN': zhCN,
  'zh-TW': { ...zhCN },
};

for (const pack of [oroMxI18n, oroMxPageI18n, oroMxGapI18n, rosaMxI18n]) {
  for (const [locale, dict] of Object.entries(pack)) {
    const target = catalogs[locale as LocaleCode];
    if (target) Object.assign(target, dict);
  }
}
Object.assign(
  catalogs['es-ES'],
  oroMxI18n['es-MX'] ?? {},
  oroMxPageI18n['es-MX'] ?? {},
  oroMxGapI18n['es-MX'] ?? {},
);
Object.assign(catalogs['zh-TW'], {
  'auth.serverError.ipDenied': '目前 IP 已被限制登入，請聯絡客服。',
  'auth.serverError.accountDisabled': '帳號已被停用，請聯絡客服。',
  'auth.serverError.invalidCredentials': '手機號或密碼錯誤，請檢查後重試。',
  'auth.serverError.smsCodeInvalid': '驗證碼錯誤，請檢查後重試。',
  'auth.serverError.passwordInvalid': '密碼錯誤，請檢查後重試。',
  'auth.serverError.userNotFound': '帳號不存在，請先註冊。',
  'auth.serverError.loginFailed': '暫時無法登入。',
  'auth.serverError.loginValidation': '請輸入有效帳號和至少 6 位的密碼。',
  'auth.serverError.rateLimited': '嘗試次數過多，請稍後再試。',
  'auth.serverError.registerFailed': '暫時無法建立帳號。',
  'auth.serverError.registerValidation': '帳號或手機號已存在，請直接登入。',
  'auth.serverError.tenantScope': '該站點暫時無法登入。',
  'auth.serverError.unavailable': '認證服務暫不可用，請稍後重試。',
});

const STORAGE_KEY = `app.locale.${clientEnv.siteId || 'default'}`;

const isSupported = (code: string): code is LocaleCode =>
  localesForCurrentSkin().some((locale) => locale.code === code);

const detectInitialLocale = (): LocaleCode => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isSupported(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  if (isSupported(clientEnv.defaultLanguage)) return clientEnv.defaultLanguage;
  return skinFallbackLocale();
};

export const currentLocale = ref<LocaleCode>(detectInitialLocale());

const applyDocumentLang = (code: LocaleCode) => {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = code;
  }
};

applyDocumentLang(currentLocale.value);

export const setLocale = (code: LocaleCode) => {
  if (!isSupported(code) || code === currentLocale.value) return;
  currentLocale.value = code;
  applyDocumentLang(code);
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* storage unavailable */
  }
};

type I18nVars = Record<string, string | number>;

const interpolate = (text: string, vars?: I18nVars): string => {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (full, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : full,
  );
};

export const t = (key: string, vars?: I18nVars): string => {
  const code = currentLocale.value;
  const dictionary = catalogs[code];
  const fallbackZh = code === 'zh-TW' ? catalogs['zh-CN'][key] : undefined;
  const skinFallback = catalogs[skinFallbackLocale()][key];
  return interpolate(dictionary?.[key] ?? fallbackZh ?? catalogs['en-US'][key] ?? skinFallback ?? key, vars);
};

/** Activity/CMS locale. Never send ops-only zh-CN as a silent player request. */
export const contentLocale = (): LocaleCode => {
  const code = currentLocale.value;
  if (code === 'zh-CN' || code === 'zh-TW') return skinFallbackLocale();
  return code;
};

/**
 * 取当前语言的译文；若该语言缺失该键，则回退到调用方提供的原文（用于运营内容这类
 * 非强制全量翻译的文案，避免出现裸 key 或被葡语覆盖）。
 */
export const tf = (key: string, fallback: string, vars?: I18nVars): string => {
  const dictionary = catalogs[currentLocale.value];
  return interpolate(dictionary?.[key] ?? fallback, vars);
};
